import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAppContext } from '../utils/AppContext';
import { socketService } from '../utils/socket';
import { mediaStorageService, StoredMediaItem } from '../utils/mediaStorage';
import { teamTemplates } from '../config/defaultTeams';
import { DEFAULT_TIMER_CONFIG } from '../config/defaults';
import SnapshotManager from './SnapshotManager';
import './ConfigPanel.css';

// URLs aceptables para media del carrusel. Bloqueamos javascript:, data:, etc.
const isSafeMediaUrl = (raw: string): boolean => {
  const url = raw.trim();
  if (!url) return false;
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) return true;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'blob:';
  } catch {
    return false;
  }
};

const ConfigPanel: React.FC = () => {
  const { config, selectedTemplate, updateConfig, changeTeamTemplate } = useAppContext();
  const [activeTab, setActiveTab] = useState<'timer' | 'bracket' | 'leaderboard' | 'carousel' | 'podium' | 'pizarra' | 'snapshot'>('timer');
  
  // Estado temporal para leaderboard que no se guarda automáticamente
  const [tempLeaderboardConfig, setTempLeaderboardConfig] = useState(config.leaderboard);
  
  // Estado temporal para pizarra que no se guarda automáticamente
  const [tempPizarraConfig, setTempPizarraConfig] = useState(config.pizarra);
  const [pizarraHasUnsavedChanges, setPizarraHasUnsavedChanges] = useState(false);
  const [pizarraSaveSuccess, setPizarraSaveSuccess] = useState(false);
  
  // Estado temporal para categorías del timer que no se guarda automáticamente
  const [tempCategories, setTempCategories] = useState(
    config.timer.categories && config.timer.categories.length > 0 
      ? config.timer.categories 
      : DEFAULT_TIMER_CONFIG.categories
  );
  const [categoriesHasUnsavedChanges, setCategoriesHasUnsavedChanges] = useState(false);
  
  // Estado para detectar cambios pendientes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Estado para estadísticas de almacenamiento
  const [storageStats, setStorageStats] = useState({ total: 0, images: 0, videos: 0, totalSize: 0 });

  const handleCarouselUpdate = useCallback((field: string, value: any) => {
    updateConfig('carousel', { [field]: value });
  }, [updateConfig]);

  // Inicializar y cargar medios desde IndexedDB
  useEffect(() => {
    const loadStoredMedia = async () => {
      try {
        const restoredMedia = await mediaStorageService.restoreMediaURLs();
        if (restoredMedia.length > 0) {
          console.log(`📁 ${restoredMedia.length} archivos restaurados desde IndexedDB`);
          
          // Convertir a formato del carousel
          const carouselMedia = restoredMedia.map(item => ({
            id: item.id,
            type: item.type,
            url: item.url,
            duration: item.duration,
            name: item.name,
            size: item.size,
            uploaded: item.uploaded
          }));
          
          // Actualizar solo si no hay medios ya configurados
          if (!config.carousel.media || config.carousel.media.length === 0) {
            handleCarouselUpdate('media', carouselMedia);
          }
        }

        // Actualizar estadísticas de almacenamiento
        const stats = await mediaStorageService.getStorageStats();
        setStorageStats(stats);
      } catch (error) {
        console.error('Error al cargar medios desde IndexedDB:', error);
      }
    };

    loadStoredMedia();
  }, [config.carousel.media, handleCarouselUpdate]);

  // Sincronizar estado temporal de categorías SOLO cuando cambia el array de
  // categorías en la config global (no cuando cambian otros campos del timer
  // como minutes/seconds/título). Antes las deps incluían todo `config.timer`,
  // por lo que editar el título reseteaba las ediciones no guardadas del usuario.
  useEffect(() => {
    if (!config.timer.categories || config.timer.categories.length === 0) {
      setTempCategories(DEFAULT_TIMER_CONFIG.categories);
      updateConfig('timer', { categories: DEFAULT_TIMER_CONFIG.categories });
    } else {
      setTempCategories(config.timer.categories);
    }
    setCategoriesHasUnsavedChanges(false);
  }, [config.timer.categories, updateConfig]);

  // Función para actualizar estadísticas
  const updateStorageStats = async () => {
    try {
      const stats = await mediaStorageService.getStorageStats();
      setStorageStats(stats);
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
    }
  };
  // Actualizar el estado temporal cuando cambie la configuración desde AppContext
  useEffect(() => {
    setTempLeaderboardConfig({
      ...config.leaderboard,
      maxVisiblePositions: config.leaderboard.maxVisiblePositions || 10
    });
    setHasUnsavedChanges(false);
  }, [config.leaderboard]);

  // Actualizar el estado temporal de pizarra cuando cambie la configuración desde AppContext
  useEffect(() => {
    setTempPizarraConfig(config.pizarra);
    setPizarraHasUnsavedChanges(false);
  }, [config.pizarra]);

  // Detectar cambios en la configuración temporal
  useEffect(() => {
    const hasChanges = JSON.stringify(tempLeaderboardConfig) !== JSON.stringify(config.leaderboard);
    setHasUnsavedChanges(hasChanges);
  }, [tempLeaderboardConfig, config.leaderboard]);

  // Detectar cambios en la configuración temporal de pizarra
  useEffect(() => {
    const hasChanges = JSON.stringify(tempPizarraConfig) !== JSON.stringify(config.pizarra);
    setPizarraHasUnsavedChanges(hasChanges);
  }, [tempPizarraConfig, config.pizarra]);

  // AppContext ya suscribe al evento configUpdate y mantiene el estado sincronizado
  // entre ventanas, así que no hace falta un listener de 'storage' aquí.
  const handleTimerUpdate = (field: string, value: any) => {
    const updatedTimer = { ...config.timer, [field]: value };
    
    // Si se cambian los minutos o segundos principales, actualizar solo las categorías sincronizadas
    if (field === 'minutes' || field === 'seconds') {
      const updatedMinutes = field === 'minutes' ? value : updatedTimer.minutes;
      const updatedSeconds = field === 'seconds' ? value : updatedTimer.seconds;
      
      // Actualizar las categorías temporales también
      const updatedTempCategories = tempCategories.map((category: any) => {
        // Solo actualizar si syncWithMain es true (o undefined, que es el valor por defecto)
        if (category.syncWithMain !== false) {
          return {
            ...category,
            minutes: updatedMinutes,
            seconds: updatedSeconds
          };
        }
        return category; // Mantener el tiempo personalizado
      });
      setTempCategories(updatedTempCategories);
      
      // Actualizar solo las categorías guardadas que tienen sincronización activada
      if (updatedTimer.categories && updatedTimer.categories.length > 0) {
        updatedTimer.categories = updatedTimer.categories.map((category: any) => {
          // Solo actualizar si syncWithMain es true (o undefined, que es el valor por defecto)
          if (category.syncWithMain !== false) {
            return {
              ...category,
              minutes: updatedMinutes,
              seconds: updatedSeconds
            };
          }
          return category; // Mantener el tiempo personalizado
        });
      }
      
      // Enviar comando de actualización de tiempo
      socketService.emit('timerCommand', {
        command: 'setTime',
        payload: {
          minutes: updatedMinutes,
          seconds: updatedSeconds
        }
      });
    }
    
    updateConfig('timer', updatedTimer);
    console.log(`⏱️ Timer actualizado - ${field}:`, value);
  };

  // Funciones para manejar categorías (con estado temporal)
  const addNewCategory = () => {
    const newCategory = {
      id: `category_${Date.now()}`,
      name: `Prueba ${tempCategories.length + 1}`,
      minutes: config.timer.minutes || 2, // Usar el tiempo principal por defecto
      seconds: config.timer.seconds || 0, // Usar el tiempo principal por defecto
      syncWithMain: true // Por defecto, sincronizar con el tiempo principal
    };
    
    const updatedCategories = [...tempCategories, newCategory];
    setTempCategories(updatedCategories);
    setCategoriesHasUnsavedChanges(true);
  };

  const removeCategory = (index: number) => {
    const updatedCategories = tempCategories.filter((_: any, i: number) => i !== index);
    setTempCategories(updatedCategories);
    setCategoriesHasUnsavedChanges(true);
  };

  const updateCategory = (index: number, field: string, value: any) => {
    const updatedCategories = [...tempCategories];
    if (updatedCategories[index]) {
      updatedCategories[index] = { ...updatedCategories[index], [field]: value };
      setTempCategories(updatedCategories);
      setCategoriesHasUnsavedChanges(true);
    }
  };

  // Función para guardar las categorías al localStorage
  const saveCategoriesChanges = () => {
    const updatedTimer = { ...config.timer, categories: tempCategories };
    updateConfig('timer', updatedTimer);
    setCategoriesHasUnsavedChanges(false);
    console.log('📋 Categorías guardadas:', tempCategories);
  };

  // Función para descartar cambios de categorías
  const discardCategoriesChanges = () => {
    setTempCategories(
      config.timer.categories && config.timer.categories.length > 0 
        ? config.timer.categories 
        : DEFAULT_TIMER_CONFIG.categories
    );
    setCategoriesHasUnsavedChanges(false);
  };

  // Función auxiliar para obtener los campos que se deben editar según el contexto
  const getFieldsToEdit = () => {
    if (config.bracket.editingSnapshot && config.bracket.activePhaseId) {
      // Si estamos editando un snapshot, usar displayFields
      return config.bracket.displayFields || [];
    } else if (config.bracket.displayMode === 'snapshot' && config.bracket.activePhaseId) {
      // Si estamos visualizando un snapshot, mostrar displayFields arriba
      return config.bracket.displayFields || [];
    } else {
      // Si estamos trabajando normalmente, usar workingFields o fields para compatibilidad
      return config.bracket.workingFields || config.bracket.fields || [];
    }
  };
  // Función auxiliar para actualizar los campos correspondientes
  const updateFieldsInContext = (newFields: any[]) => {
    if (config.bracket.editingSnapshot && config.bracket.activePhaseId) {
      // Si estamos editando un snapshot, usar handleBracketUpdate
      handleBracketUpdate('fields', newFields);
    } else if (config.bracket.displayMode === 'snapshot' && config.bracket.activePhaseId) {
      // Si estamos visualizando un snapshot, actualizar displayFields
      const updatedBracket = { 
        ...config.bracket, 
        displayFields: newFields
      };
      updateConfig('bracket', updatedBracket);
      console.log('📖 Campos de snapshot visualizado actualizados');
    } else {
      // Si estamos trabajando normalmente, actualizar workingFields directamente
      const updatedBracket = { 
        ...config.bracket, 
        workingFields: newFields,
        displayFields: config.bracket.displayMode === 'working' ? newFields : config.bracket.displayFields,
        fields: newFields // Mantener compatibilidad
      };
      updateConfig('bracket', updatedBracket);
      console.log('🔧 Estado de trabajo actualizado directamente');
    }
  };

  const handleBracketUpdate = (field: string, value: any) => {
    let updatedBracket;
    
    // Manejar la actualización de campos según el estado actual
    if (field === 'fields') {
      if (config.bracket.editingSnapshot && config.bracket.activePhaseId) {
        // Si estamos editando un snapshot, actualizar displayFields y el snapshot
        updatedBracket = { 
          ...config.bracket, 
          displayFields: value,
          fields: value // Mantener compatibilidad
        };
        
        const updatedPhases = config.bracket.phases?.map(phase => 
          phase.id === config.bracket.activePhaseId 
            ? { ...phase, fields: JSON.parse(JSON.stringify(value)) }
            : phase
        ) || [];
        
        updatedBracket.phases = updatedPhases;
        
        sendBracketCommand('updateSnapshot', { 
          phaseId: config.bracket.activePhaseId, 
          fields: value 
        });
        
        console.log('🔄 Snapshot actualizado en tiempo real:', config.bracket.activePhaseId);
      } else {
        // Si estamos trabajando normalmente, actualizar workingFields
        updatedBracket = { 
          ...config.bracket, 
          workingFields: value,
          // Si estamos en modo working, también actualizar displayFields
          ...(config.bracket.displayMode === 'working' ? { displayFields: value } : {}),
          fields: value // Mantener compatibilidad
        };
        
        console.log('🔧 Estado de trabajo actualizado');
      }
    } else {
      // Para otros campos, actualizar normalmente
      updatedBracket = { ...config.bracket, [field]: value };
    }
    
    updateConfig('bracket', updatedBracket);
    console.log(`🏆 Bracket actualizado - ${field}:`, value);
  };
  
  // Funciones para manejar el bracket árbol
  const handleTreeBracketUpdate = (phase: 'semifinal' | 'final', field: string, property: string, value: string) => {
    console.log(`🌳 Actualizando TreeBracket: ${phase}.${field}.${property} = ${value}`);
    
    const currentTreeBracket = config.bracket.treeBracket || {
      semifinal: {
        field1: { team1: '', team2: '', winner: '' },
        field2: { team1: '', team2: '', winner: '' }
      },
      final: { winner: '' }
    };

    if (phase === 'semifinal') {
      const updatedSemifinal = {
        ...currentTreeBracket.semifinal,
        [field]: {
          ...currentTreeBracket.semifinal[field as 'field1' | 'field2'],
          [property]: value
        }
      };
      
      // Si estamos cambiando un equipo y ese equipo era el ganador, limpiar el ganador
      const fieldData = updatedSemifinal[field as 'field1' | 'field2'];
      if ((property === 'team1' || property === 'team2') && 
          fieldData.winner && 
          ![fieldData.team1, fieldData.team2].includes(fieldData.winner)) {
        updatedSemifinal[field as 'field1' | 'field2'].winner = '';
      }
      
      const updatedTreeBracket = {
        ...currentTreeBracket,
        semifinal: updatedSemifinal
      };
      
      console.log('🌳 Nuevo TreeBracket:', updatedTreeBracket);
      handleBracketUpdate('treeBracket', updatedTreeBracket);
      // Enviar inmediatamente el comando del socket para sincronizar
      sendBracketCommand('updateTreeBracket', { treeBracket: updatedTreeBracket });
    } else if (phase === 'final') {
      const updatedTreeBracket = {
        ...currentTreeBracket,
        final: { ...currentTreeBracket.final, [property]: value }
      };
      
      console.log('🌳 Nuevo TreeBracket (final):', updatedTreeBracket);
      handleBracketUpdate('treeBracket', updatedTreeBracket);
      // Enviar inmediatamente el comando del socket para sincronizar
      sendBracketCommand('updateTreeBracket', { treeBracket: updatedTreeBracket });
    }
    
    console.log(`🌳 Tree Bracket actualizado - ${phase}.${field}.${property}:`, value);
  };

  // Función para guardar la configuración del bracket árbol
  const saveTreeBracketConfig = () => {
    const treeBracket = config.bracket.treeBracket;
    if (!treeBracket) {
      alert('No hay configuración de bracket árbol para guardar');
      return;
    }

    // Validar que al menos se hayan configurado los equipos de las semifinales
    const field1Valid = treeBracket.semifinal.field1.team1 && treeBracket.semifinal.field1.team2;
    const field2Valid = treeBracket.semifinal.field2.team1 && treeBracket.semifinal.field2.team2;
    
    if (!field1Valid || !field2Valid) {
      alert('Debe configurar todos los equipos de las semifinales antes de guardar');
      return;
    }

    // Enviar comando para actualizar el bracket árbol
    sendBracketCommand('updateTreeBracket', { treeBracket });
    console.log('🌳 Configuración del bracket árbol guardada:', treeBracket);
    alert('Configuración del bracket árbol guardada correctamente');
  };
  
  const handleLeaderboardUpdate = (field: string, value: any) => {
    // Solo actualizar estado temporal, NO localStorage
    setTempLeaderboardConfig(prev => ({ ...prev, [field]: value }));
    console.log(`📊 Leaderboard temporal actualizado - ${field}:`, value);
  };
  // Nueva función para guardar la configuración temporal al localStorage
  const saveLeaderboardConfig = () => {
    updateConfig('leaderboard', tempLeaderboardConfig);
    sendLeaderboardCommand('saveConfig', tempLeaderboardConfig);
    setHasUnsavedChanges(false);
    console.log('💾 Configuración de leaderboard guardada en localStorage:', tempLeaderboardConfig);
  };
  // Función para descartar cambios
  const discardLeaderboardChanges = () => {
    setTempLeaderboardConfig(config.leaderboard);
    setHasUnsavedChanges(false);
    console.log('🔄 Cambios descartados, volviendo a la configuración guardada');
  };

  const handlePodiumUpdate = (field: string, value: any) => {
    const updatedPodium = { ...config.podium, [field]: value };
    updateConfig('podium', updatedPodium);
    console.log(`🏆 Podium actualizado - ${field}:`, value);
  };

  // Funciones para manejar cambios temporales en pizarra
  const handleTempPizarraUpdate = (field: string, value: any) => {
    setTempPizarraConfig(prev => ({ ...prev, [field]: value }));
    setPizarraSaveSuccess(false); // Resetear mensaje de éxito cuando se hacen cambios
  };

  const savePizarraChanges = () => {
    // updateConfig ya se encarga de persistir en localStorage y emitir el evento
    // configUpdate con el formato canónico { section, data }.
    updateConfig('pizarra', tempPizarraConfig);
    setPizarraHasUnsavedChanges(false);
    setPizarraSaveSuccess(true);
    setTimeout(() => setPizarraSaveSuccess(false), 3000);
  };

  const discardPizarraChanges = () => {
    setTempPizarraConfig(config.pizarra);
    setPizarraHasUnsavedChanges(false);
    console.log('🔄 Cambios de pizarra descartados');
  };

  // Funciones para drag and drop de pizarras
  const handleDragStart = (e: React.DragEvent, pizarraId: string) => {
    setDraggedPizarra(pizarraId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    
    if (!draggedPizarra || draggedPizarra === targetId) {
      setDraggedPizarra(null);
      return;
    }

    const pizarras = [...(tempPizarraConfig?.pizarras || [])];
    const draggedIndex = pizarras.findIndex(p => p.id === draggedPizarra);
    const targetIndex = pizarras.findIndex(p => p.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedPizarra(null);
      return;
    }

    // Reordenar las pizarras
    const [draggedItem] = pizarras.splice(draggedIndex, 1);
    pizarras.splice(targetIndex, 0, draggedItem);

    handleTempPizarraUpdate('pizarras', pizarras);
    setDraggedPizarra(null);
  };

  const handleDragEnd = () => {
    setDraggedPizarra(null);
  };

  // Función para obtener todos los equipos disponibles de las diferentes secciones
  const getAvailableTeams = () => {
    const teams = new Set<string>();
    
    // Agregar equipos del leaderboard
    if (config.leaderboard?.teams) {
      config.leaderboard.teams.forEach((team: any) => {
        if (team.name && team.name.trim()) {
          teams.add(team.name);
        }
      });
    }
    
    // Agregar equipos del bracket
    if (config.bracket?.teams) {
      config.bracket.teams.forEach((team: any) => {
        if (team.name && team.name.trim()) {
          teams.add(team.name);
        }
      });
    }
    
    // Convertir a array y ordenar
    return Array.from(teams).sort();
  };

  // Función para obtener el color de un equipo
  const getTeamColor = (teamName: string) => {
    // Buscar en leaderboard primero
    if (config.leaderboard?.teams) {
      const team = config.leaderboard.teams.find((t: any) => t.name === teamName);
      if (team && team.color) {
        return team.color;
      }
    }
    
    // Buscar en bracket
    if (config.bracket?.teams) {
      const team = config.bracket.teams.find((t: any) => t.name === teamName);
      if (team && team.color) {
        return team.color;
      }
    }
    
    // Color por defecto si no se encuentra
    return '#007bff';
  };

  // Estado para drag and drop
  const [draggedPizarra, setDraggedPizarra] = useState<string | null>(null);

  // Funciones de comando con logs mejorados
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sendTimerCommand = (command: string, payload?: any) => {
    console.log(`🎮 Enviando comando timer: ${command}`, payload);
    socketService.emit('timerCommand', { command, payload });
  };

  // Función para navegación del timer sin auto-inicio
  const navigateAndStartTimer = (command: string, payload?: any) => {
    console.log(`🎮 Navegando timer: ${command}`, payload);
    // Solo enviar el comando de navegación, sin auto-inicio
    socketService.emit('timerCommand', { command, payload });
  };

  const sendCarouselCommand = (command: string, payload?: any) => {
    console.log(`🎮 Enviando comando carousel: ${command}`, payload);
    socketService.emit('carouselCommand', { command, payload });
  };
  const sendLeaderboardCommand = (command: string, payload?: any) => {
    console.log(`🎮 Enviando comando leaderboard: ${command}`, payload);
    socketService.emit('leaderboardCommand', { command, payload });
  };

  const sendPodiumCommand = (command: string, payload?: any) => {
    console.log(`🎮 Enviando comando podium: ${command}`, payload);
    socketService.emit('podiumCommand', { command, payload });
  };

  const sendBracketCommand = (command: string, payload?: any) => {
    console.log(`🎮 Enviando comando bracket: ${command}`, payload);
    socketService.emit('bracketCommand', { command, payload });
  };
  // Funciones para gestión de equipos
  const addTeamToBracket = () => {
    const teams = [...config.bracket.teams, { 
      name: `Equipo ${config.bracket.teams.length + 1}`,
      color: '#667eea' // Color por defecto
    }];
    handleBracketUpdate('teams', teams);
  };

  const removeTeamFromBracket = (index: number) => {
    const teams = config.bracket.teams.filter((_, i) => i !== index);
    handleBracketUpdate('teams', teams);
  };  const updateBracketTeam = (index: number, field: string, value: string) => {
    const teams = [...config.bracket.teams];
    const currentTeam = teams[index];
    
    // Asegurar que el equipo sea un objeto
    if (typeof currentTeam === 'string') {
      // Convertir de string a objeto si es necesario
      teams[index] = { name: currentTeam, color: '#667eea' };
    }
    
    // Ahora actualizar el campo
    const teamObj = teams[index] as { name: string; color?: string };
    (teamObj as any)[field] = value;
    
    handleBracketUpdate('teams', teams);
  };const addTeamToLeaderboard = () => {
    // Crear un objeto de fases inicializado en 0 basado en las fases configuradas
    const initialPhases: {[key: string]: number} = {};
    Object.keys(tempLeaderboardConfig.phaseNames).forEach(phaseKey => {
      initialPhases[phaseKey] = 0;
    });
      const teams = [...tempLeaderboardConfig.teams, { 
      name: `Equipo ${tempLeaderboardConfig.teams.length + 1}`, 
      color: '#667eea', // Color por defecto
      phases: initialPhases,
      total: 0
    }];
    handleLeaderboardUpdate('teams', teams);
  };  const updateLeaderboardTeam = (index: number, field: string, value: string | number) => {
    const teams = [...tempLeaderboardConfig.teams];
    if (field === 'name' || field === 'color') {
      teams[index] = { ...teams[index], [field]: value as string };
    } else {
      // Para cualquier campo que no sea 'name' o 'color', tratarlo como una fase
      const updatedPhases = { ...teams[index].phases, [field]: parseInt(value as string) || 0 };
      const total = Object.values(updatedPhases).reduce((sum: number, points) => sum + (typeof points === 'number' ? points : 0), 0);
      teams[index] = { ...teams[index], phases: updatedPhases, total };
    }
    handleLeaderboardUpdate('teams', teams);
  };
  const removeTeamFromLeaderboard = (index: number) => {
    const teams = tempLeaderboardConfig.teams.filter((_, i) => i !== index);
    handleLeaderboardUpdate('teams', teams);
  };
  // Añadir nueva fase
  const addPhase = () => {
    const phaseKeys = Object.keys(tempLeaderboardConfig.phaseNames);
    const nextPhaseNumber = phaseKeys.length + 1;
    const newPhaseKey = `phase${nextPhaseNumber}`;
    
    const newPhaseNames = {
      ...tempLeaderboardConfig.phaseNames,
      [newPhaseKey]: `Fase ${nextPhaseNumber}`
    };
    
    const updatedTeams = tempLeaderboardConfig.teams.map(team => ({
      ...team,
      phases: {
        ...team.phases,
        [newPhaseKey]: 0
      }
    }));
    
    setTempLeaderboardConfig(prev => ({
      ...prev,
      phaseNames: newPhaseNames,
      teams: updatedTeams
    }));
  };

  // Eliminar fase
  const removePhase = (phaseKey: string) => {
    if (Object.keys(tempLeaderboardConfig.phaseNames).length <= 2) {
      alert('Debe haber al menos 2 fases');
      return;
    }
    
    const { [phaseKey]: _, ...remainingPhases } = tempLeaderboardConfig.phaseNames as any;
    
    const updatedTeams = tempLeaderboardConfig.teams.map((team: any) => {
      const { [phaseKey]: __, ...remainingTeamPhases } = team.phases;
      const newTotal = Object.values(remainingTeamPhases).reduce((sum: number, points: any) => sum + (points || 0), 0);
      return {
        ...team,
        phases: remainingTeamPhases,
        total: newTotal
      };
    });
    
    setTempLeaderboardConfig(prev => ({
      ...prev,
      phaseNames: remainingPhases,
      teams: updatedTeams
    }));
  };
  const addMediaToCarousel = async () => {
    const url = prompt('URL del archivo multimedia:');
    if (!url) return;
    if (!isSafeMediaUrl(url)) {
      toast.error('URL no válida. Sólo se admiten http(s)://, blob: o rutas relativas.');
      return;
    }

    const typePrompt = prompt('Tipo de archivo:\n1 - Imagen\n2 - Video\n\nIngresa 1 o 2:', '1');
    const type = typePrompt === '2' ? 'video' : 'image';

    const name = prompt('Nombre del archivo (opcional):') || `${type}-${Date.now()}`;
    const duration = type === 'image'
      ? (parseInt(prompt('Duración en milisegundos (opcional, por defecto 5000):') || '5000', 10) || 5000)
      : undefined;

    try {
      // Guardar URL externa en IndexedDB
      const savedItem = await mediaStorageService.saveMediaURL(url, type, name, duration);
      
      // Convertir a formato del carousel
      const newMediaItem = {
        id: savedItem.id,
        type: savedItem.type,
        url: savedItem.url,
        duration: savedItem.duration,
        name: savedItem.name,
        uploaded: savedItem.uploaded
      };

      const media = [...config.carousel.media, newMediaItem];
      handleCarouselUpdate('media', media);
      
      // Actualizar estadísticas
      await updateStorageStats();
      
      console.log('🔗 URL añadida al carousel y guardada en IndexedDB:', url);
    } catch (error) {
      toast.error(`No se pudo guardar la URL en IndexedDB: ${error instanceof Error ? error.message : 'error desconocido'}`);
      // Fallback: agregar sin IndexedDB para no perder la acción del usuario
      const media = [...config.carousel.media, {
        id: Date.now().toString(),
        type: type as 'image' | 'video',
        url: url,
        duration: duration,
        name: name,
        uploaded: false
      }];
      handleCarouselUpdate('media', media);
    }
  };

  const updateCarouselMedia = (index: number, field: string, value: any) => {
    const media = [...config.carousel.media];
    media[index] = { ...media[index], [field]: value };
    handleCarouselUpdate('media', media);
  };  const removeMediaFromCarousel = async (index: number) => {
    const mediaToRemove = config.carousel.media[index];
    const media = config.carousel.media.filter((_, i) => i !== index);
      // Eliminar también de IndexedDB si es un archivo local
    if (mediaToRemove && mediaToRemove.uploaded) {
      try {
        await mediaStorageService.deleteMedia(mediaToRemove.id);
        console.log('🗑️ Archivo eliminado de IndexedDB:', mediaToRemove.name);
        // Actualizar estadísticas
        await updateStorageStats();
      } catch (error) {
        console.error('Error al eliminar archivo de IndexedDB:', error);
      }
    }
    
    handleCarouselUpdate('media', media);
  };
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log(`📁 Iniciando carga de ${files.length} archivo(s) con IndexedDB...`);

    try {
      // Guardar archivos en IndexedDB y obtener items con URLs válidas
      const savedMediaItems: StoredMediaItem[] = [];
      
      for (const file of Array.from(files)) {
        const savedItem = await mediaStorageService.saveMediaFile(file);
        savedMediaItems.push(savedItem);
      }

      // Convertir a formato compatible con el carousel
      const newMedia = savedMediaItems.map(item => ({
        id: item.id,
        type: item.type,
        url: item.url,
        duration: item.duration,
        name: item.name,
        size: item.size,
        uploaded: item.uploaded
      }));

      // Actualizar configuración del carousel
      const updatedMedia = [...(config.carousel.media || []), ...newMedia];
      handleCarouselUpdate('media', updatedMedia);

      // Limpiar el input para permitir seleccionar los mismos archivos de nuevo
      event.target.value = '';
      
      // Emitir evento adicional de sincronización con delay
      setTimeout(() => {
        socketService.emit('configUpdate', { 
          section: 'carousel', 
          data: { ...config.carousel, media: updatedMedia } 
        });
      }, 200);      console.log(`💾 ${newMedia.length} archivo(s) guardado(s) en IndexedDB y agregado(s) al carousel:`, newMedia.map((m: any) => m.name));
      
      // Actualizar estadísticas
      await updateStorageStats();
    } catch (error) {
      console.error('Error al cargar archivos:', error);
      alert('Error al cargar algunos archivos. Por favor, inténtalo de nuevo.');
    }
  };

  return (
    <div className="config-panel">
      <div className="config-header">
        <h2>Panel de Configuración</h2>
      </div>

      {/* Selector de Plantilla de Equipos */}
      <div className="team-template-selector">
        <div className="template-section">
          <h3>🎯 Plantilla de Equipos</h3>
          <div className="template-controls">
            <div className="form-group">
              <label>Seleccionar región:</label>
              <select
                value={selectedTemplate}
                onChange={(e) => changeTeamTemplate(e.target.value)}
                className="template-select"
              >
                {teamTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.teams.length} equipos)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>      <div className="config-tabs">        {(['timer', 'bracket', 'leaderboard', 'carousel', 'podium', 'pizarra', 'snapshot'] as const).map((tab) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''} ${
              (tab === 'leaderboard' && hasUnsavedChanges) || 
              (tab === 'pizarra' && pizarraHasUnsavedChanges) ? 'has-changes' : ''
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'timer' && '⏱️ Temporizador'}
            {tab === 'bracket' && '🏆 Bracket'}
            {tab === 'leaderboard' && (
              <>
                📊 Clasificación
                {hasUnsavedChanges && <span className="change-indicator">●</span>}
              </>
            )}
            {tab === 'carousel' && '🎬 Carousel'}
            {tab === 'podium' && '🏆 Podium'}
            {tab === 'pizarra' && (
              <>
                📝 Pizarra
                {pizarraHasUnsavedChanges && <span className="change-indicator">●</span>}
              </>
            )}
            {tab === 'snapshot' && '📸 Snapshots'}
          </button>
        ))}
      </div>

      <div className="config-content">
        {activeTab === 'timer' && (
          <div className="config-section">
            <h3>⏱️ Configuración del Timer y Diapositivas</h3>
            
            <div className="form-group">
              <label>Título:</label>
              <input
                type="text"
                value={config.timer.title}
                onChange={(e) => handleTimerUpdate('title', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Minutos:</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={config.timer.minutes}
                  onChange={(e) => handleTimerUpdate('minutes', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Segundos:</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={config.timer.seconds}
                  onChange={(e) => handleTimerUpdate('seconds', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            {/* Gestión de Categorías */}
            <div className="categories-section">
              <div className="categories-header">
                <h4>📋 Categorías/Diapositivas</h4>
                {categoriesHasUnsavedChanges && (
                  <div className="unsaved-indicator">
                    <span className="changes-dot">●</span>
                    Cambios sin guardar
                  </div>
                )}
              </div>
              
              <p className="section-description">
                Crea categorías que funcionarán como diapositivas. Entre cada categoría se mostrará la imagen de introducción.
                <br />
                <strong>💡 Nota:</strong> Por defecto, cada categoría se sincroniza con el tiempo principal, pero puedes desactivar la sincronización para establecer tiempos personalizados.
              </p>
              
              <div className="category-controls">
                <div className="category-list">
                  {tempCategories.map((category: any, index: number) => (
                    <div key={category.id} className="category-item">
                      <div className="category-header">
                        <span className="category-number">{index + 1}</span>
                        <input
                          type="text"
                          value={category.name}
                          onChange={(e) => updateCategory(index, 'name', e.target.value)}
                          placeholder="Nombre de la categoría"
                          className="category-name-input"
                        />
                        <button
                          className="btn-remove-category"
                          onClick={() => removeCategory(index)}
                          title="Eliminar categoría"
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="category-time-controls">
                        <div className="time-sync-toggle">
                          <label className="sync-checkbox">
                            <input
                              type="checkbox"
                              checked={category.syncWithMain !== false} // Por defecto true
                              onChange={(e) => updateCategory(index, 'syncWithMain', e.target.checked)}
                            />
                            🔄 Sincronizar con tiempo principal
                          </label>
                        </div>
                        <div className="time-inputs-row">
                          <div className="time-input-group">
                            <label>Min:</label>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              value={category.minutes}
                              onChange={(e) => updateCategory(index, 'minutes', parseInt(e.target.value) || 0)}
                              readOnly={category.syncWithMain !== false}
                              className={category.syncWithMain !== false ? "synced-input" : ""}
                              title={category.syncWithMain !== false ? 
                                "Este tiempo se sincroniza automáticamente. Desactiva la sincronización para editarlo." : 
                                "Tiempo personalizado para esta categoría"}
                            />
                          </div>
                          <div className="time-input-group">
                            <label>Seg:</label>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              value={category.seconds}
                              onChange={(e) => updateCategory(index, 'seconds', parseInt(e.target.value) || 0)}
                              readOnly={category.syncWithMain !== false}
                              className={category.syncWithMain !== false ? "synced-input" : ""}
                              title={category.syncWithMain !== false ? 
                                "Este tiempo se sincroniza automáticamente. Desactiva la sincronización para editarlo." : 
                                "Tiempo personalizado para esta categoría"}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="category-actions">
                  <button
                    className="btn-add-category"
                    onClick={addNewCategory}
                  >
                    ➕ Agregar Categoría
                  </button>
                  
                  {categoriesHasUnsavedChanges && (
                    <div className="save-actions">
                      <button
                        className="btn-save-categories"
                        onClick={saveCategoriesChanges}
                      >
                        💾 Guardar Cambios
                      </button>
                      <button
                        className="btn-discard-categories"
                        onClick={discardCategoriesChanges}
                      >
                        ↩️ Descartar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Controles de Navegación */}
            <div className="navigation-controls">
              <h4>🎮 Navegación</h4>
              <p className="nav-description">
                <strong> Inicio automático:</strong> El timer se reiniciará e iniciará automáticamente al cambiar de diapositiva.
              </p>
              <div className="control-buttons">
                <button 
                  className="control-btn nav"
                  onClick={() => navigateAndStartTimer('prev')}
                >
                  ⬅ Anterior
                </button>
                <button 
                  className="control-btn nav"
                  onClick={() => navigateAndStartTimer('next')}
                >
                  ➡ Siguiente
                </button>
              </div>
              
              {(config.timer.categories || []).length > 0 && (
                <div className="category-navigation">
                  <label>Ir a categoría:</label>
                  <select
                    onChange={(e) => {
                      const index = parseInt(e.target.value);
                      if (!isNaN(index)) {
                        navigateAndStartTimer('goToCategory', { index });
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="">Seleccionar...</option>
                    {(config.timer.categories || []).map((category: any, index: number) => (
                      <option key={category.id} value={index}>
                        {index + 1}. {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            {/* Sección de controles del timer oculta - se inicia automáticamente */}
            {/* 
            <div className="command-controls" style={{ display: 'none' }}>
              <h4>⏱ Controles del Timer</h4>
              <div className="control-buttons">
                <button 
                  className="control-btn start"
                  onClick={() => sendTimerCommand('start')}
                >
                  ▶ Iniciar
                </button>
                <button 
                  className="control-btn pause"
                  onClick={() => sendTimerCommand('pause')}
                >
                  ⏸ Pausar
                </button>
                <button 
                  className="control-btn reset"
                  onClick={() => sendTimerCommand('reset')}
                >
                  ⏹ Reiniciar
                </button>
              </div>
            </div>
            */}
          </div>
        )}        {activeTab === 'bracket' && (
          <div className="config-section">            {/* Panel de estado rápido */}
            <div className="bracket-status-dashboard">
              <div className="status-card">
                <div className="status-icon">🔧</div>
                <div className="status-info">
                  <span className="status-value">{config.bracket.workingFields?.length || 0}</span>
                  <span className="status-label">Trabajo</span>
                </div>
              </div>
              <div className="status-card">
                <div className="status-icon">📺</div>
                <div className="status-info">
                  <span className="status-value">{config.bracket.displayFields?.length || 0}</span>
                  <span className="status-label">Pantalla</span>
                </div>
              </div>
              <div className="status-card">
                <div className="status-icon">👥</div>
                <div className="status-info">
                  <span className="status-value">{config.bracket.teams?.length || 0}</span>
                  <span className="status-label">Equipos</span>
                </div>
              </div>
              <div className="status-card">
                <div className="status-icon">✅</div>
                <div className="status-info">
                  <span className="status-value">{getFieldsToEdit().filter(f => f.winner).length || 0}</span>
                  <span className="status-label">Completos</span>
                </div>
              </div>
              <div className="status-card">
                <div className="status-icon">📸</div>
                <div className="status-info">
                  <span className="status-value">{config.bracket.phases?.length || 0}</span>
                  <span className="status-label">Snapshots</span>
                </div>
              </div>
            </div>

            {/* Configuración General Mejorada */}
            <div className="bracket-config-section">
              <h4>⚙️ Configuración General</h4>
              <div className="modern-form-grid">                <div className="form-group">
                  <label>Título del Bracket</label>
                  <input
                    type="text"
                    value={config.bracket.title}
                    onChange={(e) => handleBracketUpdate('title', e.target.value)}
                    placeholder="Ej: NEUROTEIDE"
                  />
                </div>                <div className="form-group">
                  <label>Fase Activa</label>
                  <select
                    value={config.bracket.currentPhase || Object.keys(config.leaderboard.phaseNames)[0] || 'phase1'}
                    onChange={(e) => handleBracketUpdate('currentPhase', e.target.value)}
                  >
                    {Object.entries(config.leaderboard.phaseNames).map(([phaseKey, phaseName]) => (
                      <option key={phaseKey} value={phaseKey}>
                        {phaseName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Tipo de Bracket</label>
                  <select
                    value={config.bracket.bracketMode || 'phases'}
                    onChange={(e) => handleBracketUpdate('bracketMode', e.target.value)}
                  >
                    <option value="phases">🏟️ Por Fases (Actual)</option>
                    <option value="tree">🌳 Tipo Árbol (Semifinal y Final)</option>
                  </select>
                </div>
{/*                 <div className="form-group">
                  <label>Tipo de Torneo</label>
                  <select
                    disabled
                    value={config.bracket.tournamentType || 'single'}
                    onChange={(e) => handleBracketUpdate('tournamentType', e.target.value)}
                  >
                    <option value="single">Eliminación Simple</option>
                    <option value="double">Doble Eliminación</option>
                  </select>
                </div> */}
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.bracket.allowScores || false}
                      onChange={(e) => handleBracketUpdate('allowScores', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Permitir Puntuaciones
                  </label>
                </div>
              </div>
            </div>

            {/* Gestión de Equipos Mejorada */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>👥 Gestión de Equipos</h4>
                <div className="header-actions">
                  <button 
                    onClick={addTeamToBracket} 
                    className="action-btn primary"
                    disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                  >
                    ➕ Añadir Equipo
                  </button>
                </div>
              </div>
                <div className="teams-grid">
                {config.bracket.teams.map((team, index) => {
                  const teamName = typeof team === 'string' ? team : team.name;
                  const teamColor = typeof team === 'string' ? '#667eea' : (team.color || '#667eea');
                  
                  return (
                    <div key={index} className="team-card">
                      <div 
                        className="team-number" 
                        style={{ backgroundColor: teamColor }}
                      >
                        {index + 1}
                      </div>
                      <input
                        type="text"
                        value={teamName}
                        onChange={(e) => updateBracketTeam(index, 'name', e.target.value)}
                        placeholder="Nombre del equipo"
                        className="team-input"
                        disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                      />
                      <input
                        type="color"
                        value={teamColor}
                        onChange={(e) => updateBracketTeam(index, 'color', e.target.value)}
                        title="Color del equipo"
                        className="team-color-input"
                        disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                      />
                      <button 
                        onClick={() => removeTeamFromBracket(index)} 
                        className="remove-btn"
                        title="Eliminar equipo"
                        disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                      >
                        🗑️
                      </button>
                    </div>
                  );
                })}
                
                {!config.bracket.teams.length && (
                  <div className="empty-state">
                    <div className="empty-icon">👥</div>
                    <p>No hay equipos registrados</p>
                    <span>Añade equipos para crear el bracket</span>
                  </div>
                )}
              </div>
            </div>            {/* Gestión de Partidos Mejorada */}
            <div className="bracket-config-section">              <div className="section-header">
                <h4>
                  🏟️ Gestión de Partidos
                  {config.bracket.editingSnapshot && (
                    <span className="editing-badge"> - 📝 EDITANDO SNAPSHOT</span>
                  )}
                </h4>
                <div className="header-actions">
                  {config.bracket.editingSnapshot && config.bracket.activePhaseId && (
                    <>
                      <div className="editing-indicator">
                        <span>📝 Editando: {config.bracket.phases?.find(p => p.id === config.bracket.activePhaseId)?.title}</span>
                      </div>
                      <button 
                        className="action-btn warning"
                        onClick={() => {
                          const updatedBracket = {
                            ...config.bracket,
                            editingSnapshot: false,
                            activePhaseId: undefined,
                            fields: []
                          };
                          updateConfig('bracket', updatedBracket);
                          sendBracketCommand('exitEditMode');
                          console.log('🚪 Saliendo del modo de edición de snapshot');
                        }}
                        title="Salir del modo de edición y volver al bracket principal"
                      >
                        🚪 Salir de Edición
                      </button>
                    </>
                  )}                  <button 
                    onClick={() => {
                      const newField = {
                        id: `campo-${Date.now()}`,
                        name: `Campo ${getFieldsToEdit().length + 1}`,
                        team1: '',
                        team2: '',
                        winner: ''
                      };
                      const fields = [...getFieldsToEdit(), newField];
                      updateFieldsInContext(fields);
                    }} 
                    className="action-btn primary"
                    disabled={config.bracket.teams.length < 2 || (config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot)}
                    title={config.bracket.teams.length < 2 ? "Necesitas al menos 2 equipos" : "Crear nuevo campo"}
                  >
                    ➕ Nuevo campo
                  </button>
                  <button 
                    className="action-btn secondary"onClick={() => {
                      // Auto-asignar equipos a campos vacíos
                      const availableTeams = config.bracket.teams.map(team => 
                        typeof team === 'string' ? team : team.name
                      );                      const fields = [...getFieldsToEdit()];
                      
                      // Mezclar equipos para asignación aleatoria
                      for (let i = availableTeams.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [availableTeams[i], availableTeams[j]] = [availableTeams[j], availableTeams[i]];
                      }
                      
                      fields.forEach(field => {
                        if (!field.team1 && availableTeams.length > 0) {
                          field.team1 = availableTeams.shift() || '';
                        }
                        if (!field.team2 && availableTeams.length > 0) {
                          field.team2 = availableTeams.shift() || '';
                        }
                      });
                      
                      updateFieldsInContext(fields);
                      sendBracketCommand('autoAssign', { fields });
                    }}
                    disabled={!config.bracket.teams.length || !getFieldsToEdit().length || (config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot)}
                    title="Asignar automáticamente equipos a partidos vacíos"
                  >
                    🎯 Auto-Asignar
                  </button>
                </div>
              </div>
                <div className="matches-container">
                {getFieldsToEdit().map((field, index) => (
                  <div key={field.id} className="match-card">
                    <div className="match-header">
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => {
                          const fields = [...getFieldsToEdit()];
                          fields[index] = { ...fields[index], name: e.target.value };
                          updateFieldsInContext(fields);
                        }}
                        placeholder="Nombre del partido"
                        className="match-title-input"
                        disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                      />                      <button 
                        onClick={() => {
                          const fields = getFieldsToEdit().filter((_, i) => i !== index);
                          updateFieldsInContext(fields);
                        }}
                        className="remove-btn"
                        title="Eliminar partido"
                        disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                      >
                        🗑️
                      </button>
                    </div>
                    
                    <div className="match-teams-container">                      <div className="team-selector">
                        <label>Equipo 1</label>
                        <select
                          value={field.team1}                          onChange={(e) => {
                            const fields = [...getFieldsToEdit()];
                            fields[index] = { ...fields[index], team1: e.target.value };
                            updateFieldsInContext(fields);
                          }}
                          className={field.winner === field.team1 ? 'winner-team' : ''}
                          disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                        >
                          <option value="">Seleccionar equipo</option>
                          {config.bracket.teams.map((team, teamIndex) => {
                            const teamName = typeof team === 'string' ? team : team.name;
                            return (
                              <option key={teamIndex} value={teamName}>{teamName}</option>
                            );
                          })}
                        </select>
                      </div>
                      
                      <div className="vs-divider">
                        <span>VS</span>
                      </div>
                        <div className="team-selector">
                        <label>Equipo 2</label>
                        <select
                          value={field.team2}                          onChange={(e) => {
                            const fields = [...getFieldsToEdit()];
                            fields[index] = { ...fields[index], team2: e.target.value };
                            updateFieldsInContext(fields);
                          }}
                          className={field.winner === field.team2 ? 'winner-team' : ''}
                          disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                        >
                          <option value="">Seleccionar equipo</option>
                          {config.bracket.teams.map((team, teamIndex) => {
                            const teamName = typeof team === 'string' ? team : team.name;
                            return (
                              <option key={teamIndex} value={teamName}>{teamName}</option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    
                    {config.bracket.allowScores && (
                      <div className="score-container">
                        <label>Puntuación</label>
                        <div className="score-inputs">
                          <input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={field.score?.team1 || ''}
                            onChange={(e) => {
                              const fields = [...getFieldsToEdit()];
                              const currentScore = fields[index].score || { team1: 0, team2: 0 };
                              fields[index] = { 
                                ...fields[index], 
                                score: { 
                                  ...currentScore,
                                  team1: parseInt(e.target.value) || 0 
                                } 
                              };
                              updateFieldsInContext(fields);
                            }}
                            disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                          />
                          <span className="score-separator">-</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={field.score?.team2 || ''}
                            onChange={(e) => {
                              const fields = [...getFieldsToEdit()];
                              const currentScore = fields[index].score || { team1: 0, team2: 0 };
                              fields[index] = { 
                                ...fields[index], 
                                score: { 
                                  ...currentScore,
                                  team2: parseInt(e.target.value) || 0 
                                } 
                              };
                              updateFieldsInContext(fields);
                            }}
                            disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="winner-controls">
                      <label>Seleccionar Ganador</label>
                      <div className="winner-buttons">                        {field.team1 && (
                          <button
                            className={`winner-btn ${field.winner === field.team1 ? 'active' : ''}`}
                            onClick={() => {
                              const fields = [...getFieldsToEdit()];
                              const newWinner = field.winner === field.team1 ? '' : field.team1;
                              fields[index] = { ...fields[index], winner: newWinner };
                              updateFieldsInContext(fields);
                              sendBracketCommand('setWinner', { fieldId: field.id, winner: newWinner });
                              
                              // Si estamos editando un snapshot, enviar notificación de cambio
                              if (config.bracket.editingSnapshot && config.bracket.activePhaseId) {
                                console.log('🔄 Cambio automático guardado en snapshot');
                              }
                            }}
                            disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                          >
                            {field.team1} {field.winner === field.team1 ? '👑' : ''}
                          </button>
                        )}
                        {field.team2 && (
                          <button
                            className={`winner-btn ${field.winner === field.team2 ? 'active' : ''}`}
                            onClick={() => {
                              const fields = [...getFieldsToEdit()];
                              const newWinner = field.winner === field.team2 ? '' : field.team2;
                              fields[index] = { ...fields[index], winner: newWinner };
                              updateFieldsInContext(fields);
                              sendBracketCommand('setWinner', { fieldId: field.id, winner: newWinner });
                              
                              // Si estamos editando un snapshot, enviar notificación de cambio
                              if (config.bracket.editingSnapshot && config.bracket.activePhaseId) {
                                console.log('🔄 Cambio automático guardado en snapshot');
                              }
                            }}
                            disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                          >
                            {field.team2} {field.winner === field.team2 ? '👑' : ''}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}                
                {!getFieldsToEdit().length && (
                  <div className="empty-state">
                    <div className="empty-icon">🏟️</div>
                    <p>No hay partidos configurados</p>
                    <span>Crea tu primer partido para comenzar</span>
                  </div>
                )}
              </div>
            </div>

            {/* Configuración del Bracket Árbol - Solo visible cuando está seleccionado */}
            {config.bracket.bracketMode === 'tree' && (
              <div className="bracket-config-section">
                <div className="section-header">
                  <h4>🌳 Configuración del Bracket Árbol</h4>
                  <div className="header-actions">
                    <button 
                      className="action-btn success"
                      onClick={saveTreeBracketConfig}
                      title="Guardar configuración del bracket árbol"
                    >
                      💾 Guardar
                    </button>
                    <button 
                      className="action-btn secondary"
                      onClick={() => handleBracketUpdate('treeBracket', undefined)}
                      title="Limpiar configuración del bracket árbol"
                    >
                      🗑️ Limpiar
                    </button>
                  </div>
                </div>

                <div className="tree-bracket-config-compact">

                  {/* Configuración de Semifinales */}
                  <div className="semifinal-config">
                    <h5>Semifinales</h5>

                    <div className="semifinal-grid">
                      {/* Campo 1 */}
                      <div className="field-compact">
                        <div className="field-name-input">
                          <input
                            type="text"
                            placeholder="Nombre del campo"
                            value={config.bracket.treeBracket?.semifinal?.field1?.name || 'Campo 1'}
                            onChange={(e) => handleTreeBracketUpdate('semifinal', 'field1', 'name', e.target.value)}
                            className="field-name-input-field"
                          />
                        </div>
                        <div className="teams-row">
                          <select
                            value={config.bracket.treeBracket?.semifinal?.field1?.team1 || ''}
                            onChange={(e) => handleTreeBracketUpdate('semifinal', 'field1', 'team1', e.target.value)}
                            className="team-select"
                          >
                            <option value="">Equipo 1</option>
                            {config.bracket.teams.map((team, index) => {
                              const teamName = typeof team === 'string' ? team : team.name;
                              return (
                                <option key={index} value={teamName}>{teamName}</option>
                              );
                            })}
                          </select>
                          <span className="vs-mini">VS</span>
                          <select
                            value={config.bracket.treeBracket?.semifinal?.field1?.team2 || ''}
                            onChange={(e) => handleTreeBracketUpdate('semifinal', 'field1', 'team2', e.target.value)}
                            className="team-select"
                          >
                            <option value="">Equipo 2</option>
                            {config.bracket.teams.map((team, index) => {
                              const teamName = typeof team === 'string' ? team : team.name;
                              return (
                                <option key={index} value={teamName}>{teamName}</option>
                              );
                            })}
                          </select>
                        </div>
                        <select
                          value={config.bracket.treeBracket?.semifinal?.field1?.winner || ''}
                          onChange={(e) => handleTreeBracketUpdate('semifinal', 'field1', 'winner', e.target.value)}
                          className="winner-select"
                        >
                          <option value="">Ganador</option>
                          {config.bracket.treeBracket?.semifinal?.field1?.team1 && (
                            <option value={config.bracket.treeBracket.semifinal.field1.team1}>
                              {config.bracket.treeBracket.semifinal.field1.team1}
                            </option>
                          )}
                          {config.bracket.treeBracket?.semifinal?.field1?.team2 && (
                            <option value={config.bracket.treeBracket.semifinal.field1.team2}>
                              {config.bracket.treeBracket.semifinal.field1.team2}
                            </option>
                          )}
                        </select>
                      </div>

                      {/* Campo 2 */}
                      <div className="field-compact">
                        <div className="field-name-input">
                          <input
                            type="text"
                            placeholder="Nombre del campo"
                            value={config.bracket.treeBracket?.semifinal?.field2?.name || 'Campo 2'}
                            onChange={(e) => handleTreeBracketUpdate('semifinal', 'field2', 'name', e.target.value)}
                            className="field-name-input-field"
                          />
                        </div>
                        <div className="teams-row">
                          <select
                            value={config.bracket.treeBracket?.semifinal?.field2?.team1 || ''}
                            onChange={(e) => handleTreeBracketUpdate('semifinal', 'field2', 'team1', e.target.value)}
                            className="team-select"
                          >
                            <option value="">Equipo 1</option>
                            {config.bracket.teams.map((team, index) => {
                              const teamName = typeof team === 'string' ? team : team.name;
                              return (
                                <option key={index} value={teamName}>{teamName}</option>
                              );
                            })}
                          </select>
                          <span className="vs-mini">VS</span>
                          <select
                            value={config.bracket.treeBracket?.semifinal?.field2?.team2 || ''}
                            onChange={(e) => handleTreeBracketUpdate('semifinal', 'field2', 'team2', e.target.value)}
                            className="team-select"
                          >
                            <option value="">Equipo 2</option>
                            {config.bracket.teams.map((team, index) => {
                              const teamName = typeof team === 'string' ? team : team.name;
                              return (
                                <option key={index} value={teamName}>{teamName}</option>
                              );
                            })}
                          </select>
                        </div>
                        <select
                          value={config.bracket.treeBracket?.semifinal?.field2?.winner || ''}
                          onChange={(e) => handleTreeBracketUpdate('semifinal', 'field2', 'winner', e.target.value)}
                          className="winner-select"
                        >
                          <option value="">🏆 Ganador</option>
                          {config.bracket.treeBracket?.semifinal?.field2?.team1 && (
                            <option value={config.bracket.treeBracket.semifinal.field2.team1}>
                              {config.bracket.treeBracket.semifinal.field2.team1}
                            </option>
                          )}
                          {config.bracket.treeBracket?.semifinal?.field2?.team2 && (
                            <option value={config.bracket.treeBracket.semifinal.field2.team2}>
                              {config.bracket.treeBracket.semifinal.field2.team2}
                            </option>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Separador visual */}
                  <div className="phase-separator">
                    <div className="separator-line"></div>
                    <span className="separator-arrow">→</span>
                    <div className="separator-line"></div>
                  </div>

                  {/* Configuración de Final */}
                  <div className="final-config">
                    <h5>Final</h5>
                    <div className="final-compact">
                      
                      {/* Mensaje explicativo dinámico */}
                      {!config.bracket.treeBracket?.semifinal?.field1?.winner && !config.bracket.treeBracket?.semifinal?.field2?.winner ? (
                        <div className="final-help-message info">
                          <span className="help-icon">💡</span>
                          <div>
                            <strong>Para configurar la final:</strong>
                            <br />
                            Primero selecciona los ganadores de las semifinales, o selecciona directamente cualquier equipo como campeón.
                          </div>
                        </div>
                      ) : !config.bracket.treeBracket?.semifinal?.field1?.winner || !config.bracket.treeBracket?.semifinal?.field2?.winner ? (
                        <div className="final-help-message warning">
                          <span className="help-icon">⚠️</span>
                          <div>
                            <strong>¡Faltan ganadores de semifinal!</strong>
                            <br />
                            Selecciona el ganador de la semifinal restante para completar la configuración.
                          </div>
                        </div>
                      ) : (
                        <div className="final-help-message success">
                          <span className="help-icon">✅</span>
                          <div>
                            <strong>¡Semifinales completadas!</strong>
                            <br />
                            Ahora puedes seleccionar el campeón final entre los ganadores.
                          </div>
                        </div>
                      )}

                      <div className="finalists-display">
                        <div className="finalist-item">
                          <span className="finalist-label">Finalista 1:</span>
                          <span className="finalist-name">
                            {config.bracket.treeBracket?.semifinal?.field1?.winner || 'TBD'}
                          </span>
                        </div>
                        <span className="vs-final">VS</span>
                        <div className="finalist-item">
                          <span className="finalist-label">Finalista 2:</span>
                          <span className="finalist-name">
                            {config.bracket.treeBracket?.semifinal?.field2?.winner || 'TBD'}
                          </span>
                        </div>
                      </div>
                      <select
                        value={config.bracket.treeBracket?.final?.winner || ''}
                        onChange={(e) => handleTreeBracketUpdate('final', 'final', 'winner', e.target.value)}
                        className="champion-select"
                      >
                        <option value="">👑 Seleccionar Campeón</option>
                        
                        {/* Opción 1: Ganadores de semifinales (si están definidos) */}
                        {config.bracket.treeBracket?.semifinal?.field1?.winner && (
                          <option value={config.bracket.treeBracket.semifinal.field1.winner}>
                            🏆 {config.bracket.treeBracket.semifinal.field1.winner} (Ganador Semifinal 1)
                          </option>
                        )}
                        {config.bracket.treeBracket?.semifinal?.field2?.winner && (
                          <option value={config.bracket.treeBracket.semifinal.field2.winner}>
                            🏆 {config.bracket.treeBracket.semifinal.field2.winner} (Ganador Semifinal 2)
                          </option>
                        )}
                        
                        {/* Opción 2: Si no hay ganadores de semifinales, mostrar todos los equipos */}
                        {(!config.bracket.treeBracket?.semifinal?.field1?.winner && !config.bracket.treeBracket?.semifinal?.field2?.winner) && (
                          <>
                            <optgroup label="📋 Todos los equipos disponibles">
                              {config.bracket.teams.map((team, index) => {
                                const teamName = typeof team === 'string' ? team : team.name;
                                return (
                                  <option key={index} value={teamName}>{teamName}</option>
                                );
                              })}
                            </optgroup>
                          </>
                        )}
                        
                        {/* Opción 3: Si solo hay un ganador de semifinal, mostrar también otros equipos */}
                        {((config.bracket.treeBracket?.semifinal?.field1?.winner && !config.bracket.treeBracket?.semifinal?.field2?.winner) ||
                          (!config.bracket.treeBracket?.semifinal?.field1?.winner && config.bracket.treeBracket?.semifinal?.field2?.winner)) && (
                          <>
                            <optgroup label="📋 Otros equipos disponibles">
                              {config.bracket.teams.map((team, index) => {
                                const teamName = typeof team === 'string' ? team : team.name;
                                // No mostrar el equipo que ya está como ganador de semifinal
                                const isAlreadyWinner = teamName === config.bracket.treeBracket?.semifinal?.field1?.winner ||
                                                       teamName === config.bracket.treeBracket?.semifinal?.field2?.winner;
                                if (!isAlreadyWinner) {
                                  return (
                                    <option key={index} value={teamName}>{teamName}</option>
                                  );
                                }
                                return null;
                              })}
                            </optgroup>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Gestión de Snapshots Mejorada */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>📸 Gestión de Snapshots y Estados</h4>
                <div className="header-actions">
                  {/* Indicador del estado actual */}
                  <div className="state-indicator">
                    {config.bracket.displayMode === 'working' ? (
                      <span className="working-badge">🔧 Trabajando</span>
                    ) : (
                      <span className="snapshot-badge">📸 Snapshot</span>
                    )}
                  </div>
                  
                  {/* Botón para mostrar estado de trabajo */}
                  {config.bracket.displayMode !== 'working' && (
                    <button
                      className="action-btn primary"
                      onClick={() => {
                        // Actualizar el config local para reflejar el cambio inmediato
                        const updatedBracket = {
                          ...config.bracket,
                          displayMode: 'working',
                          activePhaseId: undefined,
                          editingSnapshot: false
                        };
                        updateConfig('bracket', updatedBracket);
                        
                        sendBracketCommand('showWorkingState');
                        console.log('🔧 Cambiando a estado de trabajo');
                      }}
                      title="Mostrar estado de trabajo actual"
                    >
                      🔧 Mostrar Estado de Trabajo
                    </button>
                  )}
                  
                  <button
                    className="action-btn success"
                    onClick={() => {
                      const snapshotName = prompt('Nombre del snapshot:', `Snapshot ${(config.bracket.phases?.length || 0) + 1}`);
                      if (snapshotName) {
                        // Usar los campos del estado de trabajo actual
                        const fieldsToSave = config.bracket.workingFields || config.bracket.fields || [];
                        const currentSnapshot = {
                          id: `snapshot-${Date.now()}`,
                          title: snapshotName,
                          fields: JSON.parse(JSON.stringify(fieldsToSave)),
                          createdAt: Date.now(),
                          description: `${fieldsToSave.filter(f => f.winner).length} partidos completos`,
                          // Guardar metadatos completos del bracket
                          bracketTitle: config.bracket.title,
                          currentPhase: config.bracket.currentPhase,
                          tournamentType: config.bracket.tournamentType,
                          allowScores: config.bracket.allowScores,
                          bracketMode: config.bracket.bracketMode,
                          treeBracket: config.bracket.treeBracket ? JSON.parse(JSON.stringify(config.bracket.treeBracket)) : undefined
                        };
                        
                        const updatedPhases = [...(config.bracket.phases || []), currentSnapshot];
                        const updatedBracket = {
                          ...config.bracket,
                          phases: updatedPhases
                        };
                        
                        updateConfig('bracket', updatedBracket);
                        sendBracketCommand('saveSnapshot', { phase: currentSnapshot });
                        
                        console.log('📸 Snapshot guardado desde estado de trabajo:', currentSnapshot);
                      }
                    }}
                    disabled={!config.bracket.workingFields?.length && !config.bracket.fields?.length}
                    title="Guardar el estado actual de trabajo como snapshot"
                  >
                    📸 Guardar Snapshot
                  </button>                  <button 
                    className="action-btn danger"
                    onClick={() => {
                      if (window.confirm('¿Estás seguro de que quieres eliminar todos los snapshots?')) {
                        const updatedBracket = {
                          ...config.bracket,
                          phases: [],
                          activePhaseId: undefined
                        };
                        
                        updateConfig('bracket', updatedBracket);
                        sendBracketCommand('clearSnapshots');
                        
                        console.log('🗑️ Todos los snapshots eliminados');
                      }
                    }}
                    disabled={!config.bracket.phases || config.bracket.phases.length === 0}
                    title="Eliminar todos los snapshots"
                  >
                    🗑️ Limpiar Todo
                  </button>
                  <button 
                    className="action-btn secondary"
                    onClick={() => {
                      if (!config.bracket.phases || config.bracket.phases.length === 0) {
                        alert('No hay snapshots para exportar');
                        return;
                      }
                      const exportData = {
                        snapshots: config.bracket.phases,
                        exportDate: new Date().toISOString(),
                        version: '1.0'
                      };
                      const dataStr = JSON.stringify(exportData, null, 2);
                      const dataBlob = new Blob([dataStr], {type: 'application/json'});
                      const url = URL.createObjectURL(dataBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `snapshots_${new Date().toISOString().split('T')[0]}.json`;
                      link.click();
                      URL.revokeObjectURL(url);
                      console.log('📤 Snapshots exportados');
                    }}
                    disabled={!config.bracket.phases || config.bracket.phases.length === 0}
                    title="Exportar todos los snapshots"
                  >
                    📤 Exportar
                  </button>
                  <button 
                    className="action-btn primary"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.json';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            try {
                              const importData = JSON.parse(e.target?.result as string);
                              if (importData.snapshots && Array.isArray(importData.snapshots)) {
                                const updatedBracket = {
                                  ...config.bracket,
                                  phases: [...(config.bracket.phases || []), ...importData.snapshots]
                                };
                                updateConfig('bracket', updatedBracket);
                                sendBracketCommand('importSnapshots', { snapshots: importData.snapshots });
                                console.log('📥 Snapshots importados:', importData.snapshots.length);
                              } else {
                                alert('Formato de archivo inválido');
                              }
                            } catch (error) {
                              alert('Error al leer el archivo');
                              console.error('Error importing snapshots:', error);
                            }
                          };
                          reader.readAsText(file);
                        }
                      };
                      input.click();
                    }}
                    title="Importar snapshots desde archivo"
                  >
                    📥 Importar
                  </button>
                </div>
              </div>
                {/* Progreso del bracket actual */}
              <div className="bracket-progress">
                <div className="progress-header">
                  <span>
                    {config.bracket.displayMode === 'working' ? 'Progreso del Trabajo' : 'Progreso del Snapshot'}
                  </span>
                  <span>{getFieldsToEdit().filter(f => f.winner).length || 0} / {getFieldsToEdit().length || 0} partidos</span>
                </div>
                <div className="progress-bar-modern">
                  <div 
                    className="progress-fill-modern"
                    style={{ 
                      width: `${Math.min(100, (getFieldsToEdit().filter(f => f.winner).length || 0) / Math.max(1, getFieldsToEdit().length || 1) * 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className="snapshots-grid">
                {config.bracket.phases && config.bracket.phases.length > 0 ? (
                  config.bracket.phases.map((phase) => (
                    <div 
                      key={phase.id} 
                      className={`snapshot-card ${phase.id === config.bracket.activePhaseId ? 'active' : ''}`}                      onClick={() => {
                        // Cargar el snapshot solo para visualización
                        const updatedBracket = {
                          ...config.bracket,
                          displayFields: JSON.parse(JSON.stringify(phase.fields)),
                          activePhaseId: phase.id,
                          displayMode: 'snapshot',
                          editingSnapshot: false
                        };
                        
                        updateConfig('bracket', updatedBracket);
                        sendBracketCommand('loadSnapshot', { phase: phase, editable: false });
                        
                        console.log('📖 Snapshot cargado para visualización:', phase);
                      }}
                    >
                      <div className="snapshot-header">
                        <h5>{phase.title}</h5>                        <div className="snapshot-actions">
                          <button
                            className="action-btn-small edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Cargar el snapshot para edición
                              const updatedBracket = {
                                ...config.bracket,
                                displayFields: JSON.parse(JSON.stringify(phase.fields)),
                                activePhaseId: phase.id,
                                displayMode: 'snapshot',
                                editingSnapshot: true
                              };
                              
                              updateConfig('bracket', updatedBracket);
                              sendBracketCommand('loadSnapshot', { phase: phase, editable: true });
                              
                              console.log('✏️ Snapshot cargado para edición:', phase);
                            }}                            title="Editar snapshot"
                          >
                            ✏️                          </button>
                          <button
                            className="action-btn-small secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newName = prompt('Nuevo nombre:', phase.title);
                              if (newName && newName !== phase.title) {
                                const updatedPhases = config.bracket.phases?.map(p => 
                                  p.id === phase.id ? { ...p, title: newName } : p
                                ) || [];
                                
                                const updatedBracket = {
                                  ...config.bracket,
                                  phases: updatedPhases
                                };
                                
                                updateConfig('bracket', updatedBracket);
                                console.log('✏️ Snapshot renombrado:', newName);
                              }
                            }}                            title="Renombrar"
                          >
                            📝
                          </button>
                          <button
                            className="action-btn-small delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`¿Eliminar el snapshot "${phase.title}"?`)) {
                                const updatedPhases = config.bracket.phases?.filter(p => p.id !== phase.id) || [];
                                const updatedBracket = {
                                  ...config.bracket,
                                  phases: updatedPhases,
                                  activePhaseId: config.bracket.activePhaseId === phase.id ? undefined : config.bracket.activePhaseId
                                };
                                
                                updateConfig('bracket', updatedBracket);
                                sendBracketCommand('deleteSnapshot', { phaseId: phase.id });
                                console.log('🗑️ Snapshot eliminado:', phase.title);
                              }
                            }}
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="snapshot-info">
                        <div className="snapshot-stat">
                          <span className="stat-icon">🏟️</span>
                          <span>{phase.fields.length} partidos</span>
                        </div>
                        <div className="snapshot-stat">
                          <span className="stat-icon">✅</span>
                          <span>{phase.fields.filter(f => f.winner).length} completos</span>
                        </div>
                      </div>
                      {phase.id === config.bracket.activePhaseId && (
                        <div className="active-indicator">
                          <span>📍 Activo</span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">📸</div>
                    <p>No hay snapshots guardados</p>
                    <span>Guarda el estado actual para crear tu primer snapshot</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}        {activeTab === 'leaderboard' && (
          <div className="config-section">
            <h3>Configuración de la Clasificación por Fases</h3>
            
            {/* Configuración General */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>⚙️ Configuración General</h4>                <div className="header-actions">                  <button 
                    className={`control-btn ${hasUnsavedChanges ? 'success' : 'primary'}`}
                    onClick={saveLeaderboardConfig}
                    title={hasUnsavedChanges ? 'Hay cambios pendientes - Click para guardar' : 'Guardando configuración actual'}
                  >
                    {hasUnsavedChanges ? '💾 Guardar Cambios' : '💾 Guardar'}
                  </button>
                  {hasUnsavedChanges && (
                    <button 
                      className="control-btn secondary"
                      onClick={discardLeaderboardChanges}
                      title="Descartar cambios y volver a la configuración guardada"
                    >
                      🔄 Descartar
                    </button>
                  )}
                </div>
              </div>
                <div className="modern-form-grid">
                <div className="form-group">
                  <label>Título de la Clasificación:</label>                  <input
                    type="text"                    value={tempLeaderboardConfig.title}
                    onChange={(e) => handleLeaderboardUpdate('title', e.target.value)}
                    placeholder="Ej: Clasificacion general"
                  />
                </div>
                <div className="form-group">
                  <label>Puestos Visibles en Pantalla:</label>
                  <select
                    value={tempLeaderboardConfig.maxVisiblePositions || 10}
                    onChange={(e) => handleLeaderboardUpdate('maxVisiblePositions', parseInt(e.target.value))}
                    title="Número máximo de posiciones a mostrar en la clasificación"
                  >
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={15}>Top 15</option>
                    <option value={20}>Top 20</option>
                    <option value={25}>Top 25</option>
                    <option value={30}>Top 30</option>
                    <option value={999}>Mostrar Todos</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tempLeaderboardConfig.autoFit || false}
                      onChange={(e) => handleLeaderboardUpdate('autoFit', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Autoajustar a pantalla
                  </label>
                </div>
              </div>{/* Configuración de Nombres de Fases - Dinámico */}
              <div className="phase-names-config">
                <h5>📝 Configuración de Nombres de Fases</h5>                <div className="phase-names-grid">
                  {Object.entries(tempLeaderboardConfig.phaseNames).map(([phaseKey, phaseName]) => (
                    <div key={phaseKey} className="form-group">
                      <label>{phaseKey.replace('phase', 'Fase ')}:</label>
                      <input
                        type="text"
                        value={phaseName || ''}
                        onChange={(e) => handleLeaderboardUpdate('phaseNames', { 
                          ...tempLeaderboardConfig.phaseNames, 
                          [phaseKey]: e.target.value 
                        })}
                        placeholder={`Nombre para ${phaseKey}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Gestión Dinámica de Fases - Añadir/Eliminar */}
              <div className="phase-management">
                <h5>🔧 Gestión de Fases</h5>
                <div className="phase-management-controls">                  <div className="phase-list">
                    {Object.entries(tempLeaderboardConfig.phaseNames).map(([phaseKey, phaseName]) => (
                      <div key={phaseKey} className="phase-item">
                        <span className="phase-name">{phaseName || phaseKey}</span>
                        <button
                          className="phase-remove-btn"
                          onClick={() => removePhase(phaseKey)}
                          disabled={Object.keys(tempLeaderboardConfig.phaseNames).length <= 2}
                          title="Eliminar fase"
                        >
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className="add-phase-btn"
                    onClick={addPhase}
                    disabled={Object.keys(tempLeaderboardConfig.phaseNames).length >= 8}
                    title="Añadir nueva fase"
                  >
                    ➕ Añadir Fase
                  </button>
                </div>
              </div>
            </div>{/* Gestión de Equipos por Fases */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>👥 Gestión de Equipos y Puntuaciones por Fases</h4>
                <div className="header-actions">
                  <button 
                    onClick={addTeamToLeaderboard} 
                    className="action-btn primary"
                    disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                  >
                    ➕ Añadir Equipo
                  </button>                  <button 
                    className={`control-btn ${hasUnsavedChanges ? 'success' : 'primary'}`}
                    onClick={saveLeaderboardConfig}
                    title={hasUnsavedChanges ? 'Hay cambios pendientes - Click para guardar' : 'Guardando configuración actual'}
                    disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                  >
                    {hasUnsavedChanges ? '💾 Guardar Equipos' : '💾 Guardar Equipos'}
                  </button>                </div>
              </div>
              
              {/* Estadísticas rápidas */}
              {tempLeaderboardConfig.teams.length > 0 && (
                <div className="leaderboard-stats">
                  <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-info">
                      <span className="stat-value">{tempLeaderboardConfig.teams.length}</span>
                      <span className="stat-label">Equipos</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">🏆</div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {Math.max(...tempLeaderboardConfig.teams.map(t => t.total))}
                      </span>
                      <span className="stat-label">Mejor</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {Math.round(tempLeaderboardConfig.teams.reduce((sum, t) => sum + t.total, 0) / tempLeaderboardConfig.teams.length)}
                      </span>
                      <span className="stat-label">Promedio</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {tempLeaderboardConfig.teams.filter(team => 
                          Object.entries(tempLeaderboardConfig.phaseNames)
                            .filter(([_, name]) => name && name.trim() !== '')
                            .every(([phaseKey]) => (team.phases[phaseKey] || 0) > 0)
                        ).length}
                      </span>
                      <span className="stat-label">Completos</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="leaderboard-teams-container">
                {tempLeaderboardConfig.teams.map((team, index) => {
                  // Calcular progreso del equipo (fases con puntuación > 0)
                  const completedPhases = Object.entries(tempLeaderboardConfig.phaseNames)
                    .filter(([_, name]) => name && name.trim() !== '')
                    .filter(([phaseKey]) => (team.phases[phaseKey] || 0) > 0).length;
                  
                  const totalPhases = Object.entries(tempLeaderboardConfig.phaseNames)
                    .filter(([_, name]) => name && name.trim() !== '').length;
                  
                  const progressPercentage = totalPhases > 0 ? (completedPhases / totalPhases) * 100 : 0;
                  
                  return (
                    <div key={index} className="leaderboard-team-card">
                      {/* Indicador de progreso */}
                      <div className="team-progress-indicator">
                        <span>{completedPhases}/{totalPhases}</span>
                        <div 
                          className={`progress-dot ${progressPercentage === 100 ? '' : 'incomplete'}`}
                          title={`${progressPercentage.toFixed(0)}% completado`}
                        />
                      </div>
                      
                      <div className="team-header">
                        <div className="team-number" style={{ backgroundColor: (team as any).color || '#667eea' }}>
                          {index + 1}
                        </div>
                        <input
                          type="text"
                          value={team.name}
                          onChange={(e) => updateLeaderboardTeam(index, 'name', e.target.value)}
                          placeholder="Nombre del equipo"
                          className="team-name-input"
                          disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                        />
                        <input
                          type="color"
                          value={(team as any).color || '#667eea'}
                          onChange={(e) => updateLeaderboardTeam(index, 'color', e.target.value)}
                          title="Color del equipo"
                          className="team-color-input"
                          disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                        />
                        <button 
                          onClick={() => removeTeamFromLeaderboard(index)} 
                          className="remove-btn"
                          title="Eliminar equipo"
                          disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                        >
                          🗑️
                        </button>
                      </div>
                      
                      <div className="phases-grid">
                        {Object.entries(tempLeaderboardConfig.phaseNames)
                          .filter(([_, name]) => name && name.trim() !== '')
                          .map(([phaseKey, phaseName]) => (
                            <div key={phaseKey} className="phase-input-group">
                              <label title={phaseName}>{phaseName}:</label>
                              <input
                                type="number"
                                min="0"
                                max="9999"
                                value={team.phases[phaseKey] || 0}
                                onChange={(e) => updateLeaderboardTeam(index, phaseKey, e.target.value)}
                                placeholder="0"
                                disabled={config.bracket.displayMode === 'snapshot' && !config.bracket.editingSnapshot}
                              />
                            </div>
                          ))
                        }
                        <div className={`total-display ${team.total > 0 ? 'updated' : ''}`}>
                          <span className="total-label">Total:</span>
                          <span className="total-value">{team.total}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {!tempLeaderboardConfig.teams.length && (
                  <div className="empty-state">
                    <div className="empty-icon">👥</div>
                    <p>No hay equipos registrados</p>
                    <span>Añade equipos para comenzar la clasificación</span>
                  </div>                )}
              </div>
            </div>
          </div>
        )}        {activeTab === 'carousel' && (
          <div className="config-section">
            <h3>Configuración Avanzada del Carousel</h3>
            
            {/* Configuración General */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>⚙️ Configuración General</h4>
              </div>
              
              <div className="modern-form-grid">
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.carousel.autoPlay}
                      onChange={(e) => handleCarouselUpdate('autoPlay', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Reproducción Automática
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Intervalo por Defecto (ms):</label>
                  <input
                    type="number"
                    min="1000"
                    step="1000"
                    value={config.carousel.interval}
                    onChange={(e) => handleCarouselUpdate('interval', parseInt(e.target.value) || 5000)}
                    title="Tiempo que se muestra cada imagen por defecto"
                  />
                </div>
                
                <div className="form-group">
                  <label>Modo de Reproducción:</label>
                  <select
                    value={config.carousel.mode || 'sequential'}
                    onChange={(e) => handleCarouselUpdate('mode', e.target.value)}
                    title="Cómo se ordenan las diapositivas"
                  >
                    <option value="sequential">🔄 Secuencial</option>
                    <option value="random">🔀 Aleatorio</option>
                  </select>
                </div>
              </div>
              
              <div className="modern-form-grid">
                <div className="form-group">
                  <label>Transición:</label>
                  <select
                    value={config.carousel.transition || 'fade'}
                    onChange={(e) => handleCarouselUpdate('transition', e.target.value)}
                    title="Tipo de transición entre diapositivas"
                  >
                    <option value="fade">🌟 Fundido</option>
                    <option value="slide">➡️ Deslizar</option>
                    <option value="zoom">🔍 Zoom</option>
                  </select>
                </div>
                
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.carousel.showControls !== false}
                      onChange={(e) => handleCarouselUpdate('showControls', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Mostrar Controles
                  </label>
                </div>
                
                <div className="form-group">                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span className="status-icon">📸</span>
                    <span className="status-value">{config.carousel.media?.length || 0}</span>
                    <span className="status-label">Archivos</span>
                    {config.carousel.media?.length > 0 && (
                      <span className="sync-indicator" title="Configuración sincronizada">✅</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Controles de Reproducción */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>🎮 Controles de Reproducción</h4>
              </div>
              
              <div className="control-buttons">
                <button 
                  className="control-btn start"
                  onClick={() => sendCarouselCommand('play')}
                  title="Iniciar reproducción automática"
                >
                  ▶ Reproducir
                </button>
                <button 
                  className="control-btn pause"
                  onClick={() => sendCarouselCommand('pause')}
                  title="Pausar reproducción"
                >
                  ⏸ Pausar
                </button>
                <button 
                  className="control-btn"
                  onClick={() => sendCarouselCommand('next')}
                  title="Pasar a la siguiente diapositiva"
                >
                  ⏭ Siguiente
                </button>
                <button 
                  className="control-btn"
                  onClick={() => sendCarouselCommand('previous')}
                  title="Volver a la diapositiva anterior"
                >
                  ⏮ Anterior
                </button>
                <button 
                  className="control-btn secondary"
                  onClick={() => sendCarouselCommand('reset')}
                  title="Volver al primer archivo"
                >
                  🔄 Reiniciar
                </button>
              </div>
            </div>
            
            {/* Gestión de Archivos */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>📁 Gestión de Archivos Multimedia</h4>
                <div className="header-actions">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    style={{ display: 'none' }}
                    id="file-upload"
                    onChange={handleFileUpload}
                  />
                  <button 
                    className="action-btn primary"
                    onClick={() => document.getElementById('file-upload')?.click()}
                    title="Cargar archivos desde tu dispositivo"
                  >
                    📁 Cargar Archivos
                  </button>                  <button 
                    className="action-btn secondary"
                    onClick={addMediaToCarousel}
                    title="Añadir archivo mediante URL"
                  >
                    🔗 Añadir URL
                  </button>
                  <button 
                    className="action-btn primary"
                    onClick={() => {
                      // Simular carga de archivos de prueba para validar sincronización
                      const testMedia = [
                        {
                          id: `test-${Date.now()}`,
                          type: 'image' as const,
                          url: 'https://via.placeholder.com/800x600/667eea/ffffff?text=Imagen+de+Prueba',
                          duration: 3000,
                          name: 'Imagen de prueba',
                          uploaded: false
                        }
                      ];
                      const updatedMedia = [...(config.carousel.media || []), ...testMedia];
                      handleCarouselUpdate('media', updatedMedia);
                      
                      // Notificar del test de sincronización
                      console.log('🧪 Test de sincronización: archivo de prueba añadido');
                    }}
                    title="Añadir archivo de prueba para validar sincronización"
                  >
                    🧪 Test Sync
                  </button>
                </div>
              </div>
              
              {config.carousel.media && config.carousel.media.length > 0 ? (
                <div className="media-grid">
                  {config.carousel.media.map((media, index) => (
                    <div key={media.id} className="media-card">
                      <div className="media-header">
                        <div className="media-info">
                          <span className="media-type-badge">
                            {media.type === 'image' ? '🖼️' : '🎬'} {media.type.toUpperCase()}
                          </span>
                          {media.uploaded && <span className="uploaded-badge">📁 Local</span>}
                        </div>
                        <div className="media-actions">
                          <button
                            className="action-btn-small secondary"
                            onClick={() => {
                              const newName = prompt('Nuevo nombre:', media.name || `${media.type}-${index + 1}`);
                              if (newName) {
                                updateCarouselMedia(index, 'name', newName);
                              }
                            }}
                            title="Renombrar archivo"
                          >
                            📝
                          </button>
                          <button
                            className="action-btn-small delete"
                            onClick={() => removeMediaFromCarousel(index)}
                            title="Eliminar archivo"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      <div className="media-config-fields">
                        <div className="form-group">
                          <label>Tipo:</label>
                          <select
                            value={media.type}
                            onChange={(e) => updateCarouselMedia(index, 'type', e.target.value)}
                          >
                            <option value="image">🖼️ Imagen</option>
                            <option value="video">🎬 Video</option>
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label>URL/Ruta:</label>
                          <input
                            type="text"
                            value={media.url}
                            onChange={(e) => updateCarouselMedia(index, 'url', e.target.value)}
                            placeholder="URL del archivo o ruta local"
                          />
                        </div>
                        
                        {media.type === 'image' && (
                          <div className="form-group">
                            <label>Duración (ms):</label>
                            <input
                              type="number"
                              min="1000"
                              step="1000"
                              value={media.duration || 5000}
                              onChange={(e) => updateCarouselMedia(index, 'duration', parseInt(e.target.value) || 5000)}
                              placeholder="Tiempo de visualización"
                            />
                          </div>
                        )}
                        
                        {media.name && (
                          <div className="form-group">
                            <label>Nombre:</label>
                            <input
                              type="text"
                              value={media.name}
                              onChange={(e) => updateCarouselMedia(index, 'name', e.target.value)}
                              placeholder="Nombre del archivo"
                            />
                          </div>
                        )}
                        
                        {media.size && (
                          <div className="media-size">
                            📊 Tamaño: {(media.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">📁</div>
                  <h4>No hay archivos multimedia</h4>
                  <p>Carga imágenes y videos para crear tu presentación</p>
                  <div className="empty-actions">
                    <button 
                      className="action-btn primary"
                      onClick={() => document.getElementById('file-upload')?.click()}
                    >
                      📁 Cargar Archivos
                    </button>                    <button 
                      className="action-btn secondary"
                      onClick={addMediaToCarousel}
                    >
                      🔗 Añadir URL
                    </button>
                    <button 
                      className="action-btn primary"
                      onClick={() => {
                        // Simular carga de archivos de prueba para validar sincronización
                        const testMedia = [
                          {
                            id: `test-${Date.now()}`,
                            type: 'image' as const,
                            url: 'https://via.placeholder.com/800x600/667eea/ffffff?text=Imagen+de+Prueba',
                            duration: 3000,
                            name: 'Imagen de prueba',
                            uploaded: false
                          }
                        ];
                        handleCarouselUpdate('media', testMedia);
                        console.log('🧪 Test de sincronización: archivo de prueba añadido');
                      }}
                      title="Añadir archivo de prueba para validar sincronización"
                    >
                      🧪 Test Sync
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Vista Previa y Configuración Avanzada */}
            {config.carousel.media && config.carousel.media.length > 0 && (
              <div className="bracket-config-section">
                <div className="section-header">
                  <h4>🎯 Configuración Avanzada</h4>
                </div>
                  <div className="advanced-config">
                  <div className="config-info">
                    <div className="info-item">
                      <span className="info-label">Total de archivos:</span>
                      <span className="info-value">{config.carousel.media.length}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Imágenes:</span>
                      <span className="info-value">{config.carousel.media.filter(m => m.type === 'image').length}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Videos:</span>
                      <span className="info-value">{config.carousel.media.filter(m => m.type === 'video').length}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Modo:</span>
                      <span className="info-value">
                        {config.carousel.mode === 'random' ? '🔀 Aleatorio' : '🔄 Secuencial'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Estadísticas de almacenamiento IndexedDB */}
                  <div className="storage-stats">
                    <h5>📊 Almacenamiento Local (IndexedDB)</h5>
                    <div className="storage-info">
                      <div className="info-item">
                        <span className="info-label">Archivos almacenados:</span>
                        <span className="info-value">{storageStats.total}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Imágenes locales:</span>
                        <span className="info-value">{storageStats.images}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Videos locales:</span>
                        <span className="info-value">{storageStats.videos}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Tamaño total:</span>
                        <span className="info-value">{(storageStats.totalSize / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    </div>
                    <button 
                      className="action-btn secondary"
                      onClick={updateStorageStats}
                      title="Actualizar estadísticas de almacenamiento"
                    >
                      🔄 Actualizar Stats
                    </button>
                  </div>
                  
                  <div className="bulk-actions">                    <button 
                      className="action-btn warning"
                      onClick={async () => {
                        if (window.confirm('¿Estás seguro de que quieres eliminar todos los archivos? Esto eliminará también los archivos almacenados localmente.')) {                          try {
                            // Limpiar IndexedDB
                            await mediaStorageService.clearAllMedia();
                            console.log('🧹 IndexedDB limpiada completamente');
                            
                            // Limpiar configuración del carousel
                            handleCarouselUpdate('media', []);
                            
                            // Actualizar estadísticas
                            await updateStorageStats();
                          } catch (error) {
                            console.error('Error al limpiar archivos:', error);
                            alert('Error al eliminar algunos archivos. Se ha limpiado la configuración.');
                            handleCarouselUpdate('media', []);
                          }
                        }
                      }}
                      disabled={config.carousel.media.length === 0}
                    >
                      🗑️ Limpiar Todo
                    </button>
                    <button 
                      className="action-btn secondary"
                      onClick={() => {
                        // Función para ordenar aleatoriamente
                        const shuffled = [...config.carousel.media].sort(() => Math.random() - 0.5);
                        handleCarouselUpdate('media', shuffled);
                      }}
                      disabled={config.carousel.media.length < 2}
                    >
                      🔀 Mezclar Orden
                    </button>                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'podium' && (
          <div className="config-section">
            <h3>Configuración del Podium</h3>
            
            {/* Configuración General */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>⚙️ Configuración General</h4>
              </div>
              
              <div className="modern-form-grid">
                <div className="form-group">
                  <label>Título del Podium:</label>
                  <input
                    type="text"
                    value={config.podium?.title || 'Podium de Ganadores'}
                    onChange={(e) => handlePodiumUpdate('title', e.target.value)}
                    placeholder="Ej: Podium de Ganadores"
                  />
                </div>
                <div className="form-group">
                  <label>Subtítulo:</label>
                  <input
                    type="text"
                    value={config.podium?.subtitle || 'Neuroteide Zona Norte'}
                    onChange={(e) => handlePodiumUpdate('subtitle', e.target.value)}
                    placeholder="Ej: Neurolimpiadas 2025"
                  />
                </div>
              </div>
            </div>

            {/* Configuración de Equipos del Podium */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>🥇 Configuración de Ganadores</h4>
                <div className="header-actions">
                  <button 
                    onClick={() => {
                      // Obtener equipos del leaderboard ordenados por puntuación
                      const sortedTeams = [...config.leaderboard.teams]
                        .filter(team => team && team.name && team.name.trim() !== '')
                        .sort((a, b) => b.total - a.total)
                        .slice(0, 3);

                      const podiumTeams = {
                        first: sortedTeams[0]?.name || '',
                        second: sortedTeams[1]?.name || '',
                        third: sortedTeams[2]?.name || ''
                      };

                      handlePodiumUpdate('teams', podiumTeams);
                    }}
                    className="action-btn primary"
                    title="Auto-asignar los 3 primeros del leaderboard"
                  >
                    🎯 Auto-Asignar del Leaderboard
                  </button>
                </div>
              </div>

              <div className="podium-teams-config">
                <div className="podium-position-config">
                  <label>🥇 Primer Lugar:</label>
                  <select
                    value={config.podium?.teams?.first || ''}
                    onChange={(e) => {
                      const teams = { ...config.podium?.teams, first: e.target.value };
                      handlePodiumUpdate('teams', teams);
                    }}
                  >
                    <option value="">Seleccionar equipo</option>
                    {config.leaderboard.teams
                      .filter(team => team && team.name && team.name.trim() !== '')
                      .map((team, index) => (
                        <option key={index} value={team.name}>{team.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div className="podium-position-config">
                  <label>🥈 Segundo Lugar:</label>
                  <select
                    value={config.podium?.teams?.second || ''}
                    onChange={(e) => {
                      const teams = { ...config.podium?.teams, second: e.target.value };
                      handlePodiumUpdate('teams', teams);
                    }}
                  >
                    <option value="">Seleccionar equipo</option>
                    {config.leaderboard.teams
                      .filter(team => team && team.name && team.name.trim() !== '')
                      .map((team, index) => (
                        <option key={index} value={team.name}>{team.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div className="podium-position-config">
                  <label>🥉 Tercer Lugar:</label>
                  <select
                    value={config.podium?.teams?.third || ''}
                    onChange={(e) => {
                      const teams = { ...config.podium?.teams, third: e.target.value };
                      handlePodiumUpdate('teams', teams);
                    }}
                  >
                    <option value="">Seleccionar equipo</option>
                    {config.leaderboard.teams
                      .filter(team => team && team.name && team.name.trim() !== '')
                      .map((team, index) => (
                        <option key={index} value={team.name}>{team.name}</option>
                      ))
                    }
                  </select>
                </div>
              </div>
            </div>            {/* Controles del Podium */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>🎬 Controles de Presentación</h4>
              </div>
              
              {/* Controles Generales */}
              <div className="podium-controls">
                <button 
                  onClick={() => sendPodiumCommand('showPodium')}
                  className="action-btn success"
                  title="Iniciar la ceremonia de premiación"
                >
                  🏆 Iniciar Ceremonia
                </button>
                <button 
                  onClick={() => sendPodiumCommand('hidePodium')}
                  className="action-btn secondary"
                  title="Ocultar la ceremonia"
                >
                  👁️ Ocultar Ceremonia
                </button>
                <button 
                  onClick={() => sendPodiumCommand('reset')}
                  className="action-btn danger"
                  title="Reiniciar la ceremonia"
                >
                  🔄 Reiniciar
                </button>
              </div>

              {/* Estado Actual */}
              {config.podium?.showPodium && (
                <div className="podium-status">
                  <div className="status-info">
                    <span className="status-label">📍 Diapositiva Actual:</span>
                    <span className="status-value">
                      {config.podium?.currentSlide !== undefined 
                        ? `${config.podium.currentSlide + 1} / 8 - ${
                            config.podium.currentSlide === 0 ? '🖼️ Imagen Intercalada' :
                            config.podium.currentSlide === 1 ? '🥉 Medalla de Bronce' :
                            config.podium.currentSlide === 2 ? '🖼️ Imagen Intercalada' :
                            config.podium.currentSlide === 3 ? '🥈 Medalla de Plata' :
                            config.podium.currentSlide === 4 ? '🖼️ Imagen Intercalada' :
                            config.podium.currentSlide === 5 ? '🥇 Medalla de Oro' :
                            '🏆 Podium Completo'
                          }`
                        : '1 / 8 - 🖼️ Imagen Intercalada'
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Controles de Diapositivas */}
              <div className="podium-slideshow-controls">
                <h5>📽️ Controles de Diapositivas</h5>
                
                <div className="slideshow-navigation">
                  <button 
                    onClick={() => sendPodiumCommand('prevSlide')}
                    className="action-btn secondary"
                    title="Diapositiva anterior"
                  >
                    ← Anterior
                  </button>
                  <button 
                    onClick={() => sendPodiumCommand('nextSlide')}
                    className="action-btn secondary"
                    title="Siguiente diapositiva"
                  >
                    Siguiente →
                  </button>
                </div>

                <div className="slide-shortcuts">
                  <h6>Ir directamente a:</h6>
                  <div className="slide-buttons">
                    <button 
                      onClick={() => sendPodiumCommand('goToSlide', { slideIndex: 0 })}
                      className="action-btn slide-btn"
                      title="Ir a imagen intercalada inicial"
                    >
                      🖼️ Imagen
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('goToSlide', { slideIndex: 1 })}
                      className="action-btn slide-btn"
                      title="Ir a medalla de bronce"
                    >
                      🥉 Bronce
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('goToSlide', { slideIndex: 3 })}
                      className="action-btn slide-btn"
                      title="Ir a medalla de plata"
                    >
                      🥈 Plata
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('goToSlide', { slideIndex: 5 })}
                      className="action-btn slide-btn"
                      title="Ir a medalla de oro"
                    >
                      🥇 Oro
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('goToSlide', { slideIndex: 7 })}
                      className="action-btn slide-btn"
                      title="Ir al podium completo"
                    >
                      🏆 Podium
                    </button>
                  </div>
                </div>
              </div>

              {/* Controles Manuales de Equipos (solo para podium completo) */}
              <div className="podium-team-controls">
                <h5>🎯 Controles Manuales (Solo Podium Completo)</h5>
                <p className="control-description">
                  Estos controles solo funcionan cuando estás en la diapositiva del podium completo
                </p>
                
                <div className="team-control-row">
                  <span className="team-control-label">🥉 Tercer Lugar</span>
                  <div className="team-control-buttons">
                    <button 
                      onClick={() => sendPodiumCommand('showThird')}
                      className="action-btn success"
                      title="Mostrar tercer lugar"
                    >
                      Mostrar
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('hideThird')}
                      className="action-btn secondary"
                      title="Ocultar tercer lugar"
                    >
                      Ocultar
                    </button>
                  </div>
                </div>

                <div className="team-control-row">
                  <span className="team-control-label">🥈 Segundo Lugar</span>
                  <div className="team-control-buttons">
                    <button 
                      onClick={() => sendPodiumCommand('showSecond')}
                      className="action-btn success"
                      title="Mostrar segundo lugar"
                    >
                      Mostrar
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('hideSecond')}
                      className="action-btn secondary"
                      title="Ocultar segundo lugar"
                    >
                      Ocultar
                    </button>
                  </div>
                </div>

                <div className="team-control-row">
                  <span className="team-control-label">🥇 Primer Lugar</span>
                  <div className="team-control-buttons">
                    <button 
                      onClick={() => sendPodiumCommand('showFirst')}
                      className="action-btn success"
                      title="Mostrar primer lugar"
                    >
                      Mostrar
                    </button>
                    <button 
                      onClick={() => sendPodiumCommand('hideFirst')}
                      className="action-btn secondary"
                      title="Ocultar primer lugar"
                    >
                      Ocultar
                    </button>
                  </div>
                </div>
              </div></div>
          </div>
        )}

        {activeTab === 'pizarra' && (
          <div className="config-section">
            <h3>Configuración de Pizarras</h3>
            
            {/* Indicador de cambios pendientes */}
            {pizarraHasUnsavedChanges && (
              <div className="unsaved-changes-banner">
                <span>⚠️ Tienes cambios sin guardar</span>
                <div className="banner-actions">
                  <button onClick={savePizarraChanges} className="action-btn success small">
                    💾 Guardar
                  </button>
                  <button onClick={discardPizarraChanges} className="action-btn secondary small">
                    🔄 Descartar
                  </button>
                </div>
              </div>
            )}

            {/* Indicador de éxito */}
            {pizarraSaveSuccess && (
              <div className="save-success-banner">
                <span>✅ Cambios guardados y aplicados correctamente</span>
              </div>
            )}
            
            {/* Configuración General */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>⚙️ Configuración General</h4>
              </div>
              
              <div className="modern-form-grid">
                <div className="form-group">
                  <label>Título:</label>
                  <input
                    type="text"
                    value={tempPizarraConfig?.title || 'Pizarras Neurolimpiadas 2025'}
                    onChange={(e) => handleTempPizarraUpdate('title', e.target.value)}
                    placeholder="Ej: Pizarras Neurolimpiadas 2025"
                  />
                </div>
                <div className="form-group">
                  <label>Subtítulo:</label>
                  <input
                    type="text"
                    value={tempPizarraConfig?.subtitle || 'Equipos'}
                    onChange={(e) => handleTempPizarraUpdate('subtitle', e.target.value)}
                    placeholder="Ej: Equipos"
                  />
                </div>
              </div>

              <div className="pizarra-display-options">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tempPizarraConfig?.showNumbers || false}
                      onChange={(e) => handleTempPizarraUpdate('showNumbers', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Mostrar números en las pizarras
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tempPizarraConfig?.showTeamNames || false}
                      onChange={(e) => handleTempPizarraUpdate('showTeamNames', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Mostrar nombres de equipos
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={tempPizarraConfig?.showScores || false}
                      onChange={(e) => handleTempPizarraUpdate('showScores', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Mostrar puntuaciones
                  </label>
                </div>
              </div>
            </div>

            {/* Configuración de Pizarras en Grid */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>📝 Pizarras ({(tempPizarraConfig?.pizarras || []).length})</h4>
                <div className="header-actions">
                  <button 
                    onClick={() => {
                      const newPizarra = {
                        id: Date.now().toString(),
                        teamName: '',
                        number: 0,
                        score: 0,
                        color: '#007bff'
                      };
                      const updatedPizarras = [...(tempPizarraConfig?.pizarras || []), newPizarra];
                      handleTempPizarraUpdate('pizarras', updatedPizarras);
                    }}
                    className="action-btn primary"
                  >
                    ➕ Agregar Pizarra
                  </button>
                  {(tempPizarraConfig?.pizarras || []).length > 0 && (
                    <button 
                      onClick={() => {
                        if (window.confirm('¿Estás seguro de que quieres eliminar todas las pizarras?')) {
                          handleTempPizarraUpdate('pizarras', []);
                        }
                      }}
                      className="action-btn danger"
                    >
                      🗑️ Limpiar Todo
                    </button>
                  )}
                </div>
              </div>

              <div className="pizarras-grid">
                {(tempPizarraConfig?.pizarras || []).map((pizarra, index) => {
                  const availableTeams = getAvailableTeams();
                  
                  return (
                    <div 
                      key={pizarra.id} 
                      className={`pizarra-card ${draggedPizarra === pizarra.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, pizarra.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, pizarra.id)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="pizarra-card-header">
                        <div className="pizarra-header-left">
                          <div className="drag-handle" title="Arrastra para reordenar">
                            ⋮⋮
                          </div>
                          <div className="pizarra-number-badge" style={{ backgroundColor: pizarra.color }}>
                            {pizarra.number}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm(`¿Eliminar la pizarra "${pizarra.teamName || 'Sin equipo'}"?`)) {
                              const updatedPizarras = tempPizarraConfig?.pizarras.filter(p => p.id !== pizarra.id) || [];
                              handleTempPizarraUpdate('pizarras', updatedPizarras);
                            }
                          }}
                          className="pizarra-delete-btn"
                          title="Eliminar pizarra"
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div className="pizarra-card-body">
                        <div className="form-group">
                          <label>Equipo:</label>
                          {availableTeams.length > 0 ? (
                            <select
                              value={pizarra.teamName}
                              onChange={(e) => {
                                const selectedTeam = e.target.value;
                                const teamColor = selectedTeam ? getTeamColor(selectedTeam) : pizarra.color;
                                const updatedPizarras = tempPizarraConfig?.pizarras.map(p => 
                                  p.id === pizarra.id ? { ...p, teamName: selectedTeam, color: teamColor } : p
                                ) || [];
                                handleTempPizarraUpdate('pizarras', updatedPizarras);
                              }}
                              className="team-name-select"
                            >
                              <option value="">Seleccionar equipo...</option>
                              {availableTeams.map(teamName => (
                                <option key={teamName} value={teamName}>
                                  {teamName}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="no-teams-message">
                              <span>⚠️ No hay equipos disponibles</span>
                              <small>Configura equipos en Leaderboard o Bracket primero</small>
                            </div>
                          )}
                        </div>
                        
                        <div className="pizarra-card-row">
                          <div className="form-group half">
                            <label>Puntos:</label>
                            <input
                              type="number"
                              min="0"
                              value={pizarra.number}
                              onChange={(e) => {
                                const updatedPizarras = tempPizarraConfig?.pizarras.map(p => 
                                  p.id === pizarra.id ? { ...p, number: parseInt(e.target.value) || 0 } : p
                                ) || [];
                                handleTempPizarraUpdate('pizarras', updatedPizarras);
                              }}
                              className="number-input"
                            />
                          </div>
                          
                          <div className="form-group half">
                            <label>Color:</label>
                            <div className="color-input-group">
                              <input
                                type="color"
                                value={pizarra.color || '#007bff'}
                                onChange={(e) => {
                                  const updatedPizarras = tempPizarraConfig?.pizarras.map(p => 
                                    p.id === pizarra.id ? { ...p, color: e.target.value } : p
                                  ) || [];
                                  handleTempPizarraUpdate('pizarras', updatedPizarras);
                                }}
                                className="color-picker"
                              />
                              <span className="color-value">{pizarra.color}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {(tempPizarraConfig?.pizarras || []).length === 0 && (
                  <div className="pizarras-empty-state">
                    <div className="empty-icon">📝</div>
                    <h4>No hay pizarras configuradas</h4>
                    <p>Haz clic en "Agregar Pizarra" para comenzar</p>
                  </div>
                )}
              </div>
            </div>

            {/* Acciones Principales */}
            <div className="bracket-config-section">
              <div className="section-header">
                <h4>💾 Guardar Cambios</h4>
              </div>
              
              <div className="pizarra-save-actions">
                <button 
                  onClick={savePizarraChanges}
                  className={`action-btn primary large ${!pizarraHasUnsavedChanges ? 'disabled' : ''}`}
                  disabled={!pizarraHasUnsavedChanges}
                >
                  💾 Guardar y Aplicar Cambios
                </button>
                <button 
                  onClick={discardPizarraChanges}
                  className={`action-btn secondary large ${!pizarraHasUnsavedChanges ? 'disabled' : ''}`}
                  disabled={!pizarraHasUnsavedChanges}
                >
                  🔄 Descartar Cambios
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'snapshot' && (
          <div className="config-section">
            <h3>📸 Gestión de Snapshots</h3>
            <p className="section-description">
              Exporta e importa el estado completo del sistema, incluyendo TODAS las configuraciones,
              títulos, descripciones, campos de texto, selectores, clasificaciones, brackets, pizarras, 
              estados de formularios y más. El snapshot preserva exactamente el estado actual de la aplicación.
            </p>
            <SnapshotManager onSnapshotImported={() => {
              // Recargar configuraciones después de importar
              console.log('🔄 Snapshot importado, recargando configuraciones...');
            }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigPanel;